import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { CHAVE_RECORRENCIA_HABILITADA } from '../hooks/useRecorrenciaHabilitada';
import s from './Organizacoes.module.css';
import { PROPOSTA_PADRAO } from '../utils/propostaComercial';
import { configEtiqueta } from '../utils/etiquetaBalanca';
import ConfigRetencaoIss from '../components/fiscal/ConfigRetencaoIss';

const REGIMES = ['Simples Nacional', 'MEI', 'Lucro Presumido', 'Lucro Real'];
// Máscara padrão do CNAE: 0000-0/00
const fmtCnae = raw => {
  const dg = String(raw || '').replace(/\D/g, '').padStart(7, '0');
  return dg.length === 7 ? `${dg.slice(0,4)}-${dg.slice(4,5)}/${dg.slice(5)}` : String(raw || '');
};
const fmtCep = raw => {
  const dg = String(raw || '').replace(/\D/g, '');
  return dg.length === 8 ? dg.replace(/^(\d{5})(\d{3})$/, '$1-$2') : '';
};
const fmtFone = raw => {
  const dg = String(raw || '').replace(/\D/g, '');
  if (dg.length === 11) return `(${dg.slice(0,2)}) ${dg.slice(2,7)}-${dg.slice(7)}`;
  if (dg.length === 10) return `(${dg.slice(0,2)}) ${dg.slice(2,6)}-${dg.slice(6)}`;
  return String(raw || '').trim();
};

// Normaliza o retorno de cada provedor de consulta de CNPJ pro mesmo formato
// — usado por buscarCnpj (BrasilAPI primeiro, ReceitaWS como alternativa
// quando a BrasilAPI não achar). A BrasilAPI usa um snapshot periódico dos
// Dados Abertos da Receita (fica alguns dias/semanas atrasada pra empresa
// recém-aberta); a ReceitaWS consulta mais perto do tempo real, mas tem
// limite de requisições no plano grátis — por isso só entra como 2ª
// tentativa, não substitui a BrasilAPI como padrão.
const normalizarBrasilApi = d => ({
  razao_social:  d.razao_social,
  nome_fantasia: d.nome_fantasia,
  ie:            d.inscricoes_estaduais?.find(i => i.ativo)?.inscricao_estadual || '',
  logradouro:    [d.descricao_tipo_de_logradouro, d.logradouro].filter(Boolean).join(' '),
  numero:        d.numero,
  complemento:   d.complemento,
  bairro:        d.bairro,
  cidade:        d.municipio,
  uf:            d.uf,
  cep:           fmtCep(d.cep),
  codigo_ibge_municipio: d.codigo_municipio_ibge ? String(d.codigo_municipio_ibge) : '',
  email:         d.email,
  telefone:      fmtFone(d.ddd_telefone_1),
  cnaePrincipal: d.cnae_fiscal ? { codigo: fmtCnae(d.cnae_fiscal), descricao: d.cnae_fiscal_descricao || '' } : null,
  cnaesSecundarios: (d.cnaes_secundarios || []).map(sec => ({ codigo: fmtCnae(sec.codigo), descricao: sec.descricao || '' })),
});
// ReceitaWS não devolve IE nem código IBGE do município nesse endpoint
// grátis, e o telefone às vezes vem "(DD) NNNN-NNNN / (0000) 0000-0000"
// (2º número placeholder vazio) — usa só o primeiro.
const normalizarReceitaWs = d => ({
  razao_social:  d.nome,
  nome_fantasia: d.fantasia,
  ie:            '',
  logradouro:    d.logradouro,
  numero:        d.numero,
  complemento:   d.complemento,
  bairro:        d.bairro,
  cidade:        d.municipio,
  uf:            d.uf,
  cep:           fmtCep(d.cep),
  codigo_ibge_municipio: '',
  email:         d.email,
  telefone:      fmtFone(String(d.telefone || '').split('/')[0]),
  cnaePrincipal: d.atividade_principal?.[0]
    ? { codigo: fmtCnae(d.atividade_principal[0].code), descricao: d.atividade_principal[0].text || '' } : null,
  cnaesSecundarios: (d.atividades_secundarias || []).map(a => ({ codigo: fmtCnae(a.code), descricao: a.text || '' })),
});
const PRESENCAS = [
  { v: '1', l: '1 – Operação presencial' },
  { v: '2', l: '2 – Operação não presencial (internet)' },
  { v: '3', l: '3 – Operação não presencial (teleatendimento)' },
  { v: '4', l: '4 – NFC-e em operação com entrega em domicílio' },
  { v: '9', l: '9 – Operação não presencial (outros)' },
];
const FINALIZACOES = [
  { v: '1', l: '1 – NF-e normal' },
  { v: '2', l: '2 – NF-e complementar' },
  { v: '3', l: '3 – NF-e de ajuste' },
  { v: '4', l: '4 – Devolução/Retorno' },
];

// Módulos da rotina Limpar Movimentações (espelho do backend).
// deps: marcar o módulo exige levar os dependentes junto (FKs estruturais).
const MODULOS_LIMPEZA = [
  { id: 'vendas',      label: 'Vendas',                  desc: 'Vendas do PDV e de pedidos, itens, pagamentos e CMV no DRE', deps: ['devolucoes', 'financeiro'] },
  { id: 'devolucoes',  label: 'Devoluções de venda',     desc: 'Devoluções e créditos de cliente' },
  { id: 'pedidos',     label: 'Pedidos / Orçamentos',    desc: 'Pedidos, orçamentos, itens e histórico (vendas mantidas são desvinculadas)' },
  { id: 'compras',     label: 'Compras',                 desc: 'Entradas de NF, devoluções de compra, créditos de fornecedor e contas a pagar das entradas' },
  { id: 'financeiro',  label: 'Financeiro',              desc: 'Contas a receber/pagar, boletos, baixas de cartão, movimentações e fechamentos de caixa' },
  { id: 'manutencoes', label: 'Manutenções de estoque',  desc: 'Ajustes manuais de estoque e suas movimentações' },
  { id: 'fiscal',      label: 'Notas fiscais emitidas (NF-e/NFC-e)', desc: 'XML, chave e numeração das notas emitidas — desmarcado, as notas ficam no Módulo Fiscal mesmo apagando vendas/devoluções/compras (a numeração não volta atrás)' },
  { id: 'dfe',         label: 'DF-e',                    desc: 'Notas baixadas da SEFAZ e reinício do controle de NSU' },
];
const TODOS_MODULOS_LIMPEZA = MODULOS_LIMPEZA.reduce((o, m) => ({ ...o, [m.id]: true }), {});

const LABELS_RESUMO_LIMPEZA = {
  vendas:                    'Vendas removidas',
  itens_venda:               'Itens de venda',
  pagamentos:                'Pagamentos',
  devolucoes:                'Devoluções de venda',
  pedidos_orcamentos:        'Pedidos / Orçamentos',
  documentos_fiscais:        'Documentos fiscais (NF/NFC-e)',
  contas_receber:            'Contas a receber',
  contas_pagar:              'Contas a pagar',
  boletos:                   'Boletos',
  baixas_cartao:             'Baixas de cartão',
  baixas_cartao_itens:       'Itens de baixa',
  movimentacoes_financeiras: 'Mov. financeiras',
  fechamentos_caixa:         'Fechamentos de caixa',
  entradas_nf:               'Entradas de NF',
  itens_entrada_nf:          'Itens de entrada NF',
  devolucoes_compra:         'Devoluções de compra',
  creditos_fornecedor:       'Créditos de fornecedor',
  manutencoes:               'Manutenções de estoque',
  movimentacoes_estoque:     'Mov. de estoque',
  lancamentos_dre:           'Lançamentos DRE',
  estoque_recalculado:       'Produtos com estoque recalculado',
  dfe_documentos:            'Documentos DF-e',
};

const ORG_VAZIO = {
  razao_social: '', nome_fantasia: '', cnpj: '', ie: '', im: '',
  regime_tributario: 'Simples Nacional',
  pis_cofins_excluir_icms: false,
  logradouro: '', numero: '', complemento: '', bairro: '',
  cidade: '', uf: '', cep: '', codigo_ibge_municipio: '',
  telefone: '', email: '', email_contador: '', site: '', logo_url: '',
  ambiente_nfe: 'homologacao',
  dfe_dias_janela: 30,
  prazo_cancel_nfe_horas: 24,
  prazo_cancel_nfce_min: 30,
  comissao_pagamento: 'emissao_venda',
  comissao_base: 'produto',
  simulacao_preco: 'markup_divisor',
  habilitar_preco_custo: false, habilitar_tls: false,
  cpfs_cnpjs_xml: '',
  aceitar_estoque_insuficiente: false,
  setor_saida_id: '',
  pdv_layout: 1,
  pdv_config: {},
  cartao_config: {},
  finalizar_venda_pedido: false,
  recorrencia_habilitada: false,
  solicitar_vendedor_pedido: false,
  boleto_conta_id: '',
  habilitar_servicos: false,
  validar_limite_credito: false,
  validar_estoque_insuficiente: false,
  validar_atraso_cliente: false,
  segmento_id: null,
  horario_funcionamento: {},
  multa_atraso_pct: 2,
  juros_atraso_pct_mes: 1,
  carencia_atraso_dias: 0,
  caixa_compartilhado: false,
  nfse_serie: '1',
  nfse_percentual_tributos_sn: '',
  nfse_aliquota_iss_retido: '',
};

// Controles de comportamento do PDV (Parâmetros → PDV → Comportamento).
// Para criar um novo checkbox, basta acrescentar uma linha aqui: a chave é
// gravada em organizacoes.pdv_config (JSONB) e lida no PDV via orgData.pdv_config.
const PDV_CONTROLES = [
  {
    key: 'pedir_quantidade',
    titulo: 'Pedir quantidade ao lançar o produto',
    desc: 'Ao informar o produto e dar Enter, o cursor vai para o campo Qtd.; ' +
          'um novo Enter confirma (em vez de gravar o item direto com a quantidade atual).',
  },
  {
    key: 'pedir_valor_unitario',
    titulo: 'Pedir valor unitário ao lançar o produto',
    desc: 'Depois da quantidade, o cursor vai para o campo Preço Unit.; ' +
          'um novo Enter grava o item (permite ajustar o preço a cada lançamento).',
  },
  {
    key: 'solicitar_vendedor',
    titulo: 'Solicitar vendedor ao finalizar a venda',
    desc: 'Ao confirmar o pagamento no F9, abre a escolha do vendedor (apenas usuários ' +
          'marcados com "Exibir no PDV" no cadastro). O vendedor, o % e o valor da ' +
          'comissão de cada item são gravados para os relatórios de comissão.',
  },
  {
    key: 'controle_mesas_comandas',
    titulo: 'Controle de Mesas e Comandas',
    desc: 'Habilita o botão F1 "Mesas/Comandas" no PDV (Layout 3): abrir uma mesa, ' +
          'lançar itens ao longo do atendimento e só fechar a venda quando o cliente ' +
          'pedir a conta. Configurações abaixo aparecem quando esta opção é marcada.',
  },
  {
    key: 'cliente_obrigatorio_comanda',
    pai: 'controle_mesas_comandas',
    titulo: 'Nome do cliente obrigatório ao abrir mesa/comanda',
    desc: 'O botão "Abrir Mesa/Comanda" só funciona com o nome do cliente preenchido. ' +
          'Desmarcado (padrão), o nome continua opcional.',
  },
  {
    key: 'taxa_entrega_habilitada',
    titulo: 'Habilitar taxa de entrega',
    desc: 'Mostra o campo "Taxa de entrega" na tela de pagamento do PDV (F9) e o botão ' +
          '"+ Taxa de entrega" em Mesas/Comandas (F1). A taxa soma no total da venda, não é ' +
          'item (não mexe em estoque) e na NFC-e sai como outras despesas. Venda com taxa ' +
          'ainda não pode ser emitida em NF-e. Desmarcado (padrão), nada muda.',
  },
  {
    key: 'nfce_contingencia',
    titulo: 'Permitir NFC-e em contingência',
    desc: 'Quando a SEFAZ não responde, aparece o botão "Emitir em contingência": o cupom sai na hora, ' +
          'marcado como contingência, e o sistema transmite sozinho assim que a SEFAZ voltar. Se a nota ' +
          'normal da mesma venda tiver sido autorizada depois, ela é cancelada por substituição.',
  },
  {
    key: 'permitir_desconto_unitario',
    titulo: 'Permitir desconto unitário',
    desc: 'Mostra o botão "…" em cada item da venda. Ele abre uma janela para dar desconto em % ou em R$, ' +
          'mudar o preço unitário, a quantidade ou o total daquele item. Total menor vira desconto; preço ou ' +
          'total maior só aumenta o valor, sem mostrar acréscimo. Desconto acima do máximo do produto continua ' +
          'pedindo a senha de administrador ao finalizar.',
  },
  {
    key: 'gerar_linha_por_lancamento',
    titulo: 'Não somar quantidade do mesmo produto — gerar uma linha por lançamento',
    desc: 'Ao lançar o mesmo produto de novo (nova pesagem na balança ou novo bipe), cria ' +
          'uma linha nova no carrinho em vez de somar a quantidade na linha já existente. ' +
          'Vale tanto para produto pesado quanto por unidade. Desmarcado (padrão), o sistema ' +
          'continua somando na mesma linha.',
  },
  {
    key: 'pedir_quantidade_codigo_interno',
    titulo: 'Pedir quantidade ao lançar produto pelo código interno',
    desc: 'Quando o produto é encontrado pelo código interno digitado na busca (não por ' +
          'código de barras/bipe), o cursor vai para o campo Qtd. antes de gravar; um novo ' +
          'Enter confirma. Bipar o código de barras continua lançando direto, sem pedir ' +
          'quantidade.',
  },
  {
    key: 'botao_pesados',
    titulo: 'Habilitar botão Pesados (F4)',
    desc: 'Mostra o botão "Pesados" no rodapé do PDV: abre uma tela com as fotos e os preços ' +
          'dos produtos pesados. Clicando no produto, o peso vem da balança (se estiver ligada) ' +
          'ou é digitado, e o item já entra na venda. Quais produtos aparecem é escolhido logo abaixo.',
  },
  {
    key: 'ocultar_desconto_f9',
    titulo: 'Ocultar tela de desconto ao pressionar F9',
    desc: 'O F9 vai direto para a tela de formas de pagamento, pulando a etapa de desconto. ' +
          'Se o operador precisar mesmo assim dar desconto, clica em "← Desconto" na tela de ' +
          'pagamento para voltar e aplicar — essa opção continua disponível, só deixa de ser ' +
          'o primeiro passo.',
  },
];
const NFCE_VAZIO = {
  id_csc: '', csc: '', indicador_presenca: '1', serie: '1',
  motivo_cancelamento_padrao: '', tipo_emissao: 'normal',
  justificativa_contingencia: '',
};
const NFE_VAZIO = {
  indicador_presenca: '1', indicador_finalizacao: '1', serie: '1',
  motivo_cancelamento_padrao: '', tipo_emissao: 'normal',
  justificativa_contingencia: '',
};

/* Select de conta bancária apta a emitir boletos (banco + carteira configurados) */
// Módulos liberados por organização — só o usuário de suporte vê esta
// sub-aba (backend: GET/PUT /organizacoes/:id/modulos).
// Configuração do MDF-e (GET/PUT /fiscal/mdfe/config — sempre a empresa do login).
const TIPOS_EMITENTE_MDFE = [
  ['2', '2 – Transporte de carga própria (a empresa leva a própria mercadoria, com veículo próprio ou motorista autônomo contratado)'],
  ['1', '1 – Prestador de serviço de transporte (transportadora: emite CT-e)'],
  ['3', '3 – Prestador de serviço com CT-e globalizado'],
];
export function ConfigMdfe() {
  const [cfg, setCfg] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api.get('/fiscal/mdfe/config')
      .then(r => setCfg({ ...r.data, tipo_transportador: r.data.tipo_transportador || '', rntrc: r.data.rntrc || '', motivo_cancelamento_padrao: r.data.motivo_cancelamento_padrao || '' }))
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao carregar a configuração do MDF-e.'));
  }, []);
  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const salvar = async () => {
    setSalvando(true); setMsg('');
    try {
      const { data } = await api.put('/fiscal/mdfe/config', {
        ...cfg, tipo_transportador: cfg.tipo_emitente === '1' ? (cfg.tipo_transportador || null) : null,
      });
      setCfg(c => ({ ...c, ...data, tipo_transportador: data.tipo_transportador || '', rntrc: data.rntrc || '' }));
      setMsg('✔ Configuração do MDF-e salva.');
    } catch (err) {
      setMsg(err.response?.data?.erro || 'Erro ao salvar.');
    } finally { setSalvando(false); }
  };
  if (!cfg) return <p style={{ fontSize: 13, padding: 20 }}>{msg || 'Carregando…'}</p>;
  return (
    <div>
      <fieldset className={s.fieldset}>
        <legend className={s.legend}>Emitente do MDF-e</legend>
        <div className={s.campo}>
          <span className={s.campoLabel}>Tipo de emitente:</span>
          <select className={s.inputErp} style={{ maxWidth: 640 }} value={cfg.tipo_emitente} onChange={e => set('tipo_emitente', e.target.value)}>
            {TIPOS_EMITENTE_MDFE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {cfg.tipo_emitente === '1' && (
          <div className={s.campo}>
            <span className={s.campoLabel}>Tipo de transportador:</span>
            <select className={s.inputErp} style={{ width: 260 }} value={cfg.tipo_transportador} onChange={e => set('tipo_transportador', e.target.value)}>
              <option value="">—</option>
              <option value="etc">ETC – Empresa de transporte</option>
              <option value="tac">TAC – Transportador autônomo</option>
              <option value="ctc">CTC – Cooperativa de transporte</option>
            </select>
          </div>
        )}
        <div className={s.campo}>
          <span className={s.campoLabel}>RNTRC da empresa:</span>
          <input className={s.inputErp} style={{ width: 140 }} value={cfg.rntrc} maxLength={8}
            onChange={e => set('rntrc', e.target.value.replace(/\D/g, ''))} placeholder="só se tiver" />
        </div>
        <p style={{ fontSize: 11, color: '#888', margin: '6px 0 0', lineHeight: 1.5 }}>
          Empresa que leva a própria mercadoria usa o tipo 2 e não precisa de RNTRC. Se contratar
          motorista autônomo, o RNTRC é o do motorista (cadastrado no proprietário do veículo), e o
          CIOT e o pagamento do frete são informados em cada MDF-e. O tipo 1 é para transportadora,
          que emite CT-e.
        </p>
      </fieldset>
      <fieldset className={s.fieldset}>
        <legend className={s.legend}>Numeração e impressão</legend>
        <div className={s.campo}>
          <span className={s.campoLabel}>Série:</span>
          <input className={s.inputErp} style={{ width: 80 }} value={cfg.serie} maxLength={3}
            onChange={e => set('serie', e.target.value.replace(/\D/g, ''))} />
        </div>
        <div className={s.campo}>
          <span className={s.campoLabel}>Cópias do DAMDFE:</span>
          <input className={s.inputErp} style={{ width: 80 }} type="number" min="1" max="5" value={cfg.copias_damdfe}
            onChange={e => set('copias_damdfe', e.target.value)} />
        </div>
        <div className={s.campo}>
          <span className={s.campoLabel}>Motivo padrão de cancelamento:</span>
          <input className={s.inputErp} style={{ width: 420 }} value={cfg.motivo_cancelamento_padrao}
            onChange={e => set('motivo_cancelamento_padrao', e.target.value)} placeholder="opcional (mínimo 15 caracteres)" />
        </div>
      </fieldset>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className={s.btnErpPrimario || s.btnErp} onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : '💾 Salvar configuração do MDF-e'}
        </button>
        {msg && <span style={{ fontSize: 13 }}>{msg}</span>}
      </div>
      <p style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
        Veículos, motoristas e proprietários ficam em Módulo Fiscal → Cadastros de Transporte; o certificado digital é o mesmo da NF-e.
      </p>
    </div>
  );
}

function ModulosLiberados({ orgId }) {
  const [modulos, setModulos] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get(`/organizacoes/${orgId}/modulos`)
      .then(r => setModulos(r.data))
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao carregar os módulos.'));
  }, [orgId]);

  const alternar = chave => setModulos(ms => ms.map(m => m.chave === chave ? { ...m, liberado: !m.liberado } : m));

  const salvar = async () => {
    setSalvando(true); setMsg('');
    try {
      await api.put(`/organizacoes/${orgId}/modulos`,
        { modulos: Object.fromEntries(modulos.map(m => [m.chave, m.liberado])) });
      setMsg('✔ Módulos salvos. Os usuários desta empresa veem a mudança ao recarregar a página.');
    } catch (err) {
      setMsg(err.response?.data?.erro || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  if (!modulos) return <p style={{ fontSize: 13 }}>{msg || 'Carregando…'}</p>;
  return (
    <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8, padding: 20, maxWidth: 560 }}>
      <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>Módulos liberados para esta empresa</strong>
      <p style={{ fontSize: 12, color: '#555', margin: '0 0 14px', lineHeight: 1.5 }}>
        Só o suporte vê esta tela e sempre enxerga todos os módulos. Para os usuários da empresa,
        um módulo desmarcado some do menu e fica bloqueado, inclusive para o admin.
      </p>
      {modulos.map(m => (
        <label key={m.chave} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={m.liberado} onChange={() => alternar(m.chave)} />
          {m.label}
        </label>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <button type="button" onClick={salvar} disabled={salvando}
          style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
          {salvando ? 'Salvando…' : 'Salvar módulos'}
        </button>
        {msg && <span style={{ fontSize: 12.5 }}>{msg}</span>}
      </div>
    </div>
  );
}

function SelectContaBoleto({ value, onChange }) {
  const [contas, setContas] = useState([]);
  useEffect(() => {
    api.get('/financeiro/contas')
      .then(({ data }) => setContas((data || []).filter(c => c.ativo && c.tipo === 'banco')))
      .catch(() => {});
  }, []);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4, minWidth: 280 }}>
      <option value="">— Sem conta padrão (escolher na venda) —</option>
      {contas.map(c => (
        <option key={c.id} value={c.id}>
          {c.nome}{c.banco_codigo ? ` (banco ${c.banco_codigo})` : ' — sem cód. banco'}
        </option>
      ))}
    </select>
  );
}

/* Select de qualquer conta financeira ativa (caixa, banco, cofre, outros) */
function SelectContaFinanceira({ value, onChange, placeholder = '— Selecione a conta —' }) {
  const [contas, setContas] = useState([]);
  useEffect(() => {
    api.get('/financeiro/contas')
      .then(({ data }) => setContas((data || []).filter(c => c.ativo)))
      .catch(() => {});
  }, []);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4, minWidth: 280 }}>
      <option value="">{placeholder}</option>
      {contas.map(c => (
        <option key={c.id} value={c.id}>{c.nome} ({c.tipo})</option>
      ))}
    </select>
  );
}

export default function CadastroOrganizacao({ orgId } = {}) {
  const params = useParams();
  const id = orgId ?? params.id;
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const queryClient = useQueryClient();
  const isEdicao = Boolean(id);
  const isAdmin = usuario?.perfil === 'admin';

  const [aba, setAba]           = useState('geral');
  const [org, setOrg]           = useState(ORG_VAZIO);
  const [setoresEstoque, setSetoresEstoque] = useState([]);
  const [nfce, setNfce]         = useState(NFCE_VAZIO);
  const [nfe, setNfe]           = useState(NFE_VAZIO);
  const [numeracao, setNumeracao] = useState([]);
  const [numeracaoCaixasNfce, setNumeracaoCaixasNfce] = useState([]);
  const [carregando, setCarregando] = useState(isEdicao);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro]         = useState('');
  const [sucesso, setSucesso]   = useState('');
  const [sincronizandoMesas, setSincronizandoMesas] = useState(false);
  const [sincronizandoComandas, setSincronizandoComandas] = useState(false);
  const [segmentos, setSegmentos] = useState([]);
  const [novoSegmento, setNovoSegmento] = useState(null); // null = fechado; {nome, habilita_agendamento} = form aberto
  const [salvandoSegmento, setSalvandoSegmento] = useState(false);
  const [erroSegmento, setErroSegmento] = useState('');

  // CNPJ lookup
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);

  // XML list
  const [novoXml, setNovoXml]   = useState('');

  // Numeracao new row (só NF-e — NFC-e virou somente-leitura, a série nasce
  // ao configurar o caixa em Financeiro → Contas)
  const [novaNumNfe, setNovaNumNfe] = useState({ serie: '001', proximo_numero: '1' });
  const [novaNumNfse, setNovaNumNfse] = useState({ serie: '1', proximo_numero: '1' });

  // Logo
  const [logoFile, setLogoFile]             = useState(null);
  const [uploadandoLogo, setUploadandoLogo] = useState(false);
  const [logoErro, setLogoErro]             = useState('');
  const [logoSucesso, setLogoSucesso]       = useState('');

  // Proposta Comercial (Parâmetros → Vendas) — salva por endpoint próprio
  const [proposta, setProposta] = useState({ ativa: false, config: {} });
  const [propostaImgEnviando, setPropostaImgEnviando] = useState('');
  const setPropCfg = (campo, valor) => setProposta(p => ({ ...p, config: { ...p.config, [campo]: valor } }));
  const ligarProposta = (ativa) => setProposta(p => ({
    ativa,
    // Ao ligar pela 1ª vez, já vem com os textos sugeridos pra editar
    config: ativa && !Object.keys(p.config || {}).length ? { ...PROPOSTA_PADRAO } : p.config,
  }));
  const enviarImagemProposta = async (campo, arquivo) => {
    if (!arquivo) return;
    if (!id) { setErro('Salve a organização antes de enviar a imagem.'); return; }
    setPropostaImgEnviando(campo);
    try {
      const form = new FormData();
      form.append('imagem', arquivo);
      const { data } = await api.post(`/organizacoes/${id}/proposta-imagem?campo=${campo}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const chave = campo === 'rodape' ? 'imagem_rodape_url' : 'imagem_cabecalho_url';
      setPropCfg(chave, data[chave]);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao enviar a imagem.');
    } finally {
      setPropostaImgEnviando('');
    }
  };

  // Exclusão
  const [modalExcluir, setModalExcluir]   = useState(false);
  const [senhaExcluir, setSenhaExcluir]   = useState('');
  const [excluindo, setExcluindo]         = useState(false);
  const [erroExcluir, setErroExcluir]     = useState('');

  // Copiar configurações de outra organização
  const [modalCopiarConfig, setModalCopiarConfig] = useState(false);
  const [orgsParaCopiar, setOrgsParaCopiar]       = useState([]);
  const [origemCopiaId, setOrigemCopiaId]         = useState('');
  const [copiandoConfig, setCopiandoConfig]       = useState(false);
  const [erroCopiarConfig, setErroCopiarConfig]   = useState('');

  // CNAEs
  const [cnaes, setCnaes] = useState([]);
  const [novoCnaeCodigo, setNovoCnaeCodigo]       = useState('');
  const [novoCnaeDescricao, setNovoCnaeDescricao] = useState('');
  const addCnae = (codigo, descricao, principal = false) => {
    codigo = (codigo || '').trim();
    if (!codigo) return;
    setCnaes(prev => prev.some(c => c.codigo === codigo)
      ? prev
      : [...prev, { codigo, descricao: descricao || '', principal }]);
  };
  const removerCnae = codigo => setCnaes(prev => prev.filter(c => c.codigo !== codigo));
  const marcarPrincipal = codigo =>
    setCnaes(prev => prev.map(c => ({ ...c, principal: c.codigo === codigo })));

  // Parâmetros / Rotinas
  const [subAbaParam, setSubAbaParam]       = useState('rotinas');
  const [modalLimpar, setModalLimpar]       = useState(false);
  const [senhaLimpar, setSenhaLimpar]       = useState('');
  const [confirmaLimpar, setConfirmaLimpar] = useState(false);
  const [modulosLimpar, setModulosLimpar]   = useState({ ...TODOS_MODULOS_LIMPEZA });
  const [limpando, setLimpando]             = useState(false);
  const [erroLimpar, setErroLimpar]         = useState('');
  const [resultadoLimpar, setResultadoLimpar] = useState(null);

  const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
  const logoSrc = org.logo_url
    ? (org.logo_url.startsWith('http') ? org.logo_url : `${BACKEND_URL}${org.logo_url}`)
    : '';

  // Reaproveitado tanto pelo botão "Enviar Logo" (upload avulso) quanto
  // pelo botão geral "Salvar" — antes, escolher o arquivo e clicar direto
  // em "Salvar" (sem notar o botão pequeno "Enviar Logo") mostrava
  // "Organização salva com sucesso!" mas o arquivo escolhido nunca era
  // enviado de verdade, então a logo nunca mudava.
  const enviarLogoSelecionada = async (orgId) => {
    if (!logoFile) return true;
    const form = new FormData();
    form.append('logo', logoFile);
    const { data } = await api.post(`/organizacoes/${orgId}/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setOrg(prev => ({ ...prev, logo_url: data.logo_url }));
    setLogoFile(null);
    return true;
  };

  const uploadLogo = async () => {
    if (!logoFile) { setLogoErro('Selecione uma imagem.'); return; }
    if (!id)       { setLogoErro('Salve a organização antes de enviar a logo.'); return; }
    setLogoErro(''); setLogoSucesso(''); setUploadandoLogo(true);
    try {
      await enviarLogoSelecionada(id);
      setLogoSucesso('Logo enviada com sucesso!');
      setTimeout(() => setLogoSucesso(''), 3000);
    } catch (err) {
      setLogoErro(err.response?.data?.erro || 'Erro ao enviar logo.');
    } finally {
      setUploadandoLogo(false);
    }
  };

  // Certificado digital
  const [certInfo, setCertInfo]             = useState(null);
  const [certFile, setCertFile]             = useState(null);
  const [certSenha, setCertSenha]           = useState('');
  const [certSenhaVisivel, setCertSenhaVisivel] = useState(false);
  const [uploadandoCert, setUploadandoCert] = useState(false);
  const [certErro, setCertErro]             = useState('');
  const [certSucesso, setCertSucesso]       = useState('');

  useEffect(() => {
    api.get('/setores-estoque').then(r => setSetoresEstoque(r.data)).catch(() => {});
    api.get('/segmentos').then(r => setSegmentos(r.data)).catch(() => setSegmentos([]));
  }, []);

  // Novo segmento de negócio — criado direto do formulário, sem precisar
  // de tela própria (Administrativo → Segmentos ainda não existe).
  const criarSegmento = async () => {
    const nome = (novoSegmento?.nome || '').trim();
    if (!nome) { setErroSegmento('Informe o nome do segmento.'); return; }
    setSalvandoSegmento(true);
    setErroSegmento('');
    try {
      const { data } = await api.post('/segmentos', {
        nome, habilita_agendamento: !!novoSegmento?.habilita_agendamento,
      });
      setSegmentos(prev => [...prev, data]);
      setO('segmento_id', data.id);
      setNovoSegmento(null);
    } catch (err) {
      setErroSegmento(err.response?.data?.erro || 'Erro ao criar segmento.');
    } finally {
      setSalvandoSegmento(false);
    }
  };

  useEffect(() => {
    if (!isEdicao) return;
    setCarregando(true);
    api.get(`/organizacoes/${id}`)
      .then(({ data }) => {
        setProposta({ ativa: !!data.proposta_comercial_ativa, config: data.proposta_config || {} });
        setOrg({
          razao_social:     data.razao_social     ?? '',
          nome_fantasia:    data.nome_fantasia     ?? '',
          cnpj:             data.cnpj              ?? '',
          ie:               data.ie                ?? '',
          im:               data.im                ?? '',
          regime_tributario: data.regime_tributario ?? 'Simples Nacional',
          pis_cofins_excluir_icms: !!data.pis_cofins_excluir_icms,
          logradouro:       data.logradouro        ?? '',
          numero:           data.numero            ?? '',
          complemento:      data.complemento       ?? '',
          bairro:           data.bairro            ?? '',
          cidade:           data.cidade            ?? '',
          uf:               data.uf                ?? '',
          cep:              data.cep               ?? '',
          codigo_ibge_municipio: data.codigo_ibge_municipio ?? '',
          telefone:         data.telefone          ?? '',
          email:            data.email             ?? '',
          email_contador:   data.email_contador    ?? '',
          site:             data.site              ?? '',
          logo_url:         data.logo_url          ?? '',
          ambiente_nfe:     data.ambiente_nfe      ?? 'homologacao',
          dfe_dias_janela:  data.dfe_dias_janela   ?? 30,
          prazo_cancel_nfe_horas: data.prazo_cancel_nfe_horas ?? 24,
          prazo_cancel_nfce_min:  data.prazo_cancel_nfce_min  ?? 30,
          comissao_pagamento:     data.comissao_pagamento     ?? 'emissao_venda',
          comissao_base:          data.comissao_base          ?? 'produto',
          simulacao_preco:        data.simulacao_preco        ?? 'markup_divisor',
          habilitar_preco_custo: data.habilitar_preco_custo ?? false,
          habilitar_tls:    data.habilitar_tls     ?? false,
          cpfs_cnpjs_xml:   data.cpfs_cnpjs_xml    ?? '',
          aceitar_estoque_insuficiente: data.aceitar_estoque_insuficiente ?? false,
          setor_saida_id: data.setor_saida_id ?? '',
          pdv_layout: data.pdv_layout ?? 1,
          pdv_config: data.pdv_config ?? {},
          cartao_config: data.cartao_config ?? {},
          finalizar_venda_pedido: data.finalizar_venda_pedido ?? false,
          recorrencia_habilitada: data.recorrencia_habilitada ?? false,
          solicitar_vendedor_pedido: data.solicitar_vendedor_pedido ?? false,
          boleto_conta_id: data.boleto_conta_id ?? '',
          habilitar_servicos: data.habilitar_servicos ?? false,
          validar_limite_credito: data.validar_limite_credito ?? false,
          validar_estoque_insuficiente: data.validar_estoque_insuficiente ?? false,
          validar_atraso_cliente: data.validar_atraso_cliente ?? false,
          segmento_id: data.segmento_id ?? null,
          horario_funcionamento: data.horario_funcionamento ?? {},
          multa_atraso_pct: data.multa_atraso_pct ?? 2,
          juros_atraso_pct_mes: data.juros_atraso_pct_mes ?? 1,
          carencia_atraso_dias: data.carencia_atraso_dias ?? 0,
          caixa_compartilhado: data.caixa_compartilhado ?? false,
          nfse_serie: data.nfse_serie ?? '1',
          nfse_percentual_tributos_sn: data.nfse_percentual_tributos_sn ?? '',
          nfse_aliquota_iss_retido: data.nfse_aliquota_iss_retido ?? '',
        });
        if (data.nfce) setNfce({ ...NFCE_VAZIO, ...data.nfce });
        if (data.nfe)  setNfe({ ...NFE_VAZIO, ...data.nfe });
        if (data.numeracao) setNumeracao(data.numeracao);
        if (data.numeracaoCaixasNfce) setNumeracaoCaixasNfce(data.numeracaoCaixasNfce);
        if (data.cnaes) setCnaes(data.cnaes);
        // Carrega info do certificado
        api.get(`/fiscal/organizacoes/${data.id}/certificado`)
          .then(({ data: cert }) => cert.existe && setCertInfo(cert))
          .catch(() => {});
      })
      .catch(() => setErro('Erro ao carregar organização.'))
      .finally(() => setCarregando(false));
  }, [id, isEdicao]);

  const setO = (campo, valor) => setOrg(prev => ({ ...prev, [campo]: valor }));
  const setN = (campo, valor) => setNfce(prev => ({ ...prev, [campo]: valor }));
  const setE = (campo, valor) => setNfe(prev => ({ ...prev, [campo]: valor }));

  // ── Upload certificado ───────────────────────────────────────
  const uploadCertificado = async () => {
    if (!certFile) { setCertErro('Selecione um arquivo .pfx ou .p12.'); return; }
    if (!certSenha) { setCertErro('Informe a senha do certificado.'); return; }
    if (!id) { setCertErro('Salve a organização antes de enviar o certificado.'); return; }
    setCertErro(''); setCertSucesso(''); setUploadandoCert(true);
    try {
      const form = new FormData();
      form.append('certificado', certFile);
      form.append('senha', certSenha);
      const { data } = await api.post(`/fiscal/organizacoes/${id}/certificado`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCertInfo(data);
      setCertSenha('');
      setCertFile(null);
      setCertSucesso('Certificado enviado e validado com sucesso!');
    } catch (err) {
      setCertErro(err.response?.data?.erro || 'Erro ao enviar certificado.');
    } finally {
      setUploadandoCert(false);
    }
  };

  // ── CNPJ lookup ──────────────────────────────────────────────
  const buscarCnpj = async () => {
    const cnpj = org.cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) { setErro('Informe um CNPJ com 14 dígitos.'); return; }
    setErro(''); setBuscandoCnpj(true);
    try {
      // BrasilAPI primeiro (rápida, sem limite de requisições) — se não
      // achar (empresa aberta há pouco tempo, ainda fora do snapshot dos
      // Dados Abertos da Receita que ela usa), tenta a ReceitaWS como
      // alternativa antes de desistir. Caso real: CNPJ aberto há 2 semanas,
      // 404 na BrasilAPI, achado normalmente na ReceitaWS.
      let d = null, fonte = null;
      const rBrasilApi = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (rBrasilApi.ok) {
        d = normalizarBrasilApi(await rBrasilApi.json());
        fonte = 'brasilapi';
      } else {
        // ReceitaWS não manda Access-Control-Allow-Origin — chamar direto
        // daqui falha com "Failed to fetch" sem detalhe nenhum (achado em
        // produção, parkemporio, CNPJ aberto em 31/08/2026). Por isso passa
        // pelo backend (consultaCnpjReceitaWs), que já não tem esse problema
        // (chamada servidor-a-servidor, sem CORS envolvido).
        try {
          const { data: jReceitaWs } = await api.get(`/organizacoes/consulta-cnpj-receitaws/${cnpj}`);
          if (jReceitaWs && jReceitaWs.status !== 'ERROR') {
            d = normalizarReceitaWs(jReceitaWs);
            fonte = 'receitaws';
          }
        } catch { /* também não achou na ReceitaWS — cai no throw genérico abaixo */ }
      }
      if (!d) throw new Error('CNPJ não encontrado (consultado na BrasilAPI e na ReceitaWS) — confira o número ou preencha manualmente.');

      setOrg(prev => ({
        ...prev,
        razao_social:  d.razao_social  || prev.razao_social,
        nome_fantasia: d.nome_fantasia || prev.nome_fantasia,
        ie:            d.ie            || prev.ie,
        logradouro:    d.logradouro    || prev.logradouro,
        numero:        d.numero        || prev.numero,
        complemento:   d.complemento   || prev.complemento,
        bairro:        d.bairro        || prev.bairro,
        cidade:        d.cidade        || prev.cidade,
        uf:            d.uf            || prev.uf,
        cep:           d.cep           || prev.cep,
        codigo_ibge_municipio: d.codigo_ibge_municipio || prev.codigo_ibge_municipio,
        email:         d.email         || prev.email,
        telefone:      d.telefone      || prev.telefone,
      }));

      // CNAEs já vêm na consulta — aproveita em vez de fazer a usuária
      // digitar de novo (mesmo campo, seja qual for a fonte que achou).
      const novos = [];
      if (d.cnaePrincipal) novos.push({ ...d.cnaePrincipal, principal: true });
      for (const sec of d.cnaesSecundarios) novos.push({ ...sec, principal: false });
      if (novos.length) {
        setCnaes(prev => {
          const existentes = new Set(prev.map(c => c.codigo));
          return [...prev, ...novos.filter(n => !existentes.has(n.codigo))];
        });
      }
      if (fonte === 'receitaws') {
        setSucesso('Achado na ReceitaWS (empresa recente, ainda fora da base da BrasilAPI) — confira IE e complete manualmente, essa fonte não traz esses 2 campos.');
        setTimeout(() => setSucesso(''), 6000);
      }
    } catch (err) {
      setErro(err.message || 'Erro ao consultar CNPJ.');
    } finally {
      setBuscandoCnpj(false);
    }
  };

  // ── XML list helpers ─────────────────────────────────────────
  const xmlList = org.cpfs_cnpjs_xml
    ? org.cpfs_cnpjs_xml.split(',').map(x => x.trim()).filter(Boolean)
    : [];

  const addXml = () => {
    if (!novoXml.trim()) return;
    const lista = [...xmlList, novoXml.trim()];
    setO('cpfs_cnpjs_xml', lista.join(', '));
    setNovoXml('');
  };

  const removeXml = idx => {
    const lista = xmlList.filter((_, i) => i !== idx);
    setO('cpfs_cnpjs_xml', lista.join(', '));
  };

  // ── Numeracao helpers ────────────────────────────────────────
  const addNumeracao = (tipoDoc, nova, setNova) => {
    if (!nova.serie.trim()) return;
    setNumeracao(prev => [...prev, {
      tipo_doc: tipoDoc,
      serie: nova.serie,
      proximo_numero: parseInt(nova.proximo_numero) || 1,
      ultimo_nf: null,
    }]);
    setNova({ serie: '001', proximo_numero: '1' });
  };

  const removeNumeracao = idx => setNumeracao(prev => prev.filter((_, i) => i !== idx));

  // ── Excluir ──────────────────────────────────────────────────
  const excluirOrg = async () => {
    if (!senhaExcluir.trim()) { setErroExcluir('Digite sua senha para confirmar.'); return; }
    setErroExcluir(''); setExcluindo(true);
    try {
      await api.delete(`/organizacoes/${id}`, { data: { senha: senhaExcluir } });
      navigate('/admin/organizacoes');
    } catch (err) {
      setErroExcluir(err.response?.data?.erro || 'Erro ao excluir organização.');
      setExcluindo(false);
    }
  };

  // ── Copiar configurações de outra organização ───────────────
  // Pensado pra 2ª/3ª/4ª loja do mesmo cliente, ainda sem operação de
  // verdade — evita reconfigurar Rotinas/PDV/Vendas/Parâmetros/Bandeiras
  // na mão. Substitui tudo isso de vez; Plano de Contas é exceção (só
  // acrescenta o que falta, nunca apaga) — ver organizacoesController.
  // copiarConfiguracoes no backend.
  const abrirModalCopiarConfig = () => {
    setErroCopiarConfig(''); setOrigemCopiaId('');
    setModalCopiarConfig(true);
    api.get('/organizacoes')
      .then(({ data }) => setOrgsParaCopiar((data || []).filter(o => String(o.id) !== String(id))))
      .catch(() => setErroCopiarConfig('Erro ao carregar a lista de organizações.'));
  };

  const copiarConfig = async () => {
    if (!origemCopiaId) { setErroCopiarConfig('Escolha a organização de origem.'); return; }
    setErroCopiarConfig(''); setCopiandoConfig(true);
    try {
      const { data } = await api.post(`/organizacoes/${id}/copiar-configuracoes`, { origem_id: origemCopiaId });
      setModalCopiarConfig(false);
      setSucesso(
        `Configurações copiadas! ${data.contas_adicionadas} conta(s) nova(s) no plano de contas, ` +
        `${data.bandeiras_copiadas} bandeira(s) de cartão copiada(s). Recarregando a tela…`
      );
      setTimeout(() => window.location.reload(), 1800);
    } catch (err) {
      setErroCopiarConfig(err.response?.data?.erro || 'Erro ao copiar configurações.');
    } finally {
      setCopiandoConfig(false);
    }
  };

  // ── Rotina: Limpar Movimentações ────────────────────────────
  // Marcar/desmarcar módulo respeitando dependências (vendas ⇒ devoluções + financeiro)
  const toggleModuloLimpar = (modId) => {
    setModulosLimpar(prev => {
      const ligando = !prev[modId];
      const novo = { ...prev, [modId]: ligando };
      if (ligando) {
        // liga também os dependentes estruturais
        (MODULOS_LIMPEZA.find(m => m.id === modId)?.deps || []).forEach(d => { novo[d] = true; });
      } else {
        // desliga quem depende deste módulo
        MODULOS_LIMPEZA.filter(m => (m.deps || []).includes(modId)).forEach(m => { novo[m.id] = false; });
      }
      return novo;
    });
  };

  const modulosSelecionados = MODULOS_LIMPEZA.filter(m => modulosLimpar[m.id]);

  const executarLimpeza = async () => {
    if (!senhaLimpar.trim()) { setErroLimpar('Digite sua senha para confirmar.'); return; }
    if (!confirmaLimpar) { setErroLimpar('Marque a caixa de confirmação.'); return; }
    if (!modulosSelecionados.length) { setErroLimpar('Selecione ao menos um módulo para limpar.'); return; }
    setErroLimpar(''); setLimpando(true);
    try {
      const { data } = await api.post(`/organizacoes/${id}/rotinas/limpar-movimentacoes`,
        { senha: senhaLimpar, modulos: modulosSelecionados.map(m => m.id) });
      setResultadoLimpar(data.resumo);
      setModalLimpar(false);
      setSenhaLimpar('');
      setConfirmaLimpar(false);
    } catch (err) {
      setErroLimpar(err.response?.data?.erro || 'Erro ao executar limpeza.');
    } finally {
      setLimpando(false);
    }
  };

  // Resume o resultado de POST /mesas/sincronizar — que agora sincroniza pra
  // cima E pra baixo (cria o que falta, reativa o que voltou a caber na
  // quantidade, desativa o excedente que não estiver ocupado no momento).
  const montarResumoSincronizacao = (data, singular) => {
    const partes = [];
    if (data.criadas > 0) partes.push(`${data.criadas} ${singular}(s) nova(s) criada(s)`);
    if (data.reativadas > 0) partes.push(`${data.reativadas} reativada(s)`);
    if (data.desativadas > 0) partes.push(`${data.desativadas} desativada(s) por exceder a quantidade`);
    let msg = partes.length ? `${partes.join(', ')}.` : 'Nenhuma mudança — a numeração já estava correta.';
    if (data.ocupadas?.length) {
      msg += ` Atenção: ${data.ocupadas.length} número(s) além da quantidade estão ocupados agora (${data.ocupadas.join(', ')})`
        + ' e não foram desativados — sincronize de novo depois que liberarem.';
    }
    return msg;
  };

  // ── Mesas: gerar/sincronizar numeração ─────────────────────────
  const sincronizarMesas = async () => {
    const quantidade = parseInt(org.pdv_config?.quantidade_mesas) || 0;
    if (!quantidade) { setErro('Informe a quantidade de mesas antes de sincronizar.'); return; }
    setErro(''); setSucesso(''); setSincronizandoMesas(true);
    try {
      const { data } = await api.post('/mesas/sincronizar', {
        quantidade, prefixo: org.pdv_config?.prefixo_mesa || 'MESA', tipo: 'mesa',
      });
      setSucesso(montarResumoSincronizacao(data, 'mesa'));
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao sincronizar mesas.');
    } finally {
      setSincronizandoMesas(false);
    }
  };

  // ── Comandas (avulsas, sem mesa): gerar/sincronizar numeração ──
  const sincronizarComandas = async () => {
    const quantidade = parseInt(org.pdv_config?.quantidade_comandas) || 0;
    if (!quantidade) { setErro('Informe a quantidade de comandas antes de sincronizar.'); return; }
    setErro(''); setSucesso(''); setSincronizandoComandas(true);
    try {
      const { data } = await api.post('/mesas/sincronizar', {
        quantidade, prefixo: org.pdv_config?.prefixo_comandas || 'COMANDA', tipo: 'comanda',
      });
      setSucesso(montarResumoSincronizacao(data, 'comanda'));
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao sincronizar comandas.');
    } finally {
      setSincronizandoComandas(false);
    }
  };

  // ── Salvar ───────────────────────────────────────────────────
  const salvar = async () => {
    if (!org.razao_social.trim()) { setErro('Razão Social é obrigatória.'); return; }
    setErro(''); setSucesso(''); setSalvando(true);
    const payload = { ...org, nfce, nfe, numeracao, cnaes };
    try {
      let orgId = id;
      if (isEdicao) {
        await api.put(`/organizacoes/${id}`, payload);
      } else {
        const { data } = await api.post('/organizacoes', payload);
        orgId = data.id;
      }
      // Se uma imagem foi escolhida no campo Logo mas o usuário não clicou
      // no botão pequeno "Enviar Logo", envia ela agora — o Salvar geral
      // cuida de tudo que está pendente na tela, não só do texto.
      if (logoFile) {
        try {
          await enviarLogoSelecionada(orgId);
        } catch (err) {
          setErro(err.response?.data?.erro || 'Organização salva, mas houve erro ao enviar a logo.');
          if (!isEdicao) navigate(`/admin/organizacoes/editar/${orgId}`, { replace: true });
          return;
        }
      }
      try {
        await api.put(`/organizacoes/${orgId}/proposta`, {
          proposta_comercial_ativa: proposta.ativa, proposta_config: proposta.config,
        });
      } catch (err) {
        setErro(err.response?.data?.erro || 'Organização salva, mas houve erro ao salvar a Proposta Comercial.');
        if (!isEdicao) navigate(`/admin/organizacoes/editar/${orgId}`, { replace: true });
        return;
      }
      queryClient.invalidateQueries({ queryKey: [CHAVE_RECORRENCIA_HABILITADA] });
      if (!isEdicao) navigate(`/admin/organizacoes/editar/${orgId}`, { replace: true });
      setSucesso('Organização salva com sucesso!');
      setTimeout(() => setSucesso(''), 3000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar organização.');
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>🏢</span> {isEdicao ? 'Editar Organização' : 'Nova Organização'}</div>
      <div className={s.conteudo} style={{ padding: 20 }}>Carregando...</div>
    </div>
  );

  return (
    <div className={s.erpWrap}>
      {/* Title */}
      <div className={s.titleBar}>
        <span>🏢</span>
        {isEdicao ? `Organização — ${org.razao_social || '...'}` : 'Nova Organização'}
      </div>

      {/* Tabs */}
      <div className={s.tabBar}>
        {[
          ['geral',       'Geral'],
          ['nfce',        'NFC-e'],
          ['nfe',         'NF-e'],
          ['nfse',        'NFS-e'],
          ['mdfe',        'MDF-e'],
          ['certificado', 'Certificado Digital'],
          ['parametros',  'Parâmetros'],
        ].map(([key, label]) => (
          <button key={key}
            className={`${s.tab} ${aba === key ? s.tabAtiva : ''}`}
            onClick={() => setAba(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className={s.tabContent}>

        {/* ── ABA GERAL ── */}
        {aba === 'geral' && (
          <div className={s.formGrid}>
            {/* Coluna esquerda */}
            <div>
              {/* Identificação */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Identificação</legend>

                <div className={s.campo}>
                  <span className={s.campoLabel}>Razão Social:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={org.razao_social}
                    onChange={e => setO('razao_social', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Nome Fantasia:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={org.nome_fantasia}
                    onChange={e => setO('nome_fantasia', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>CNPJ:</span>
                  <input className={s.inputErp} style={{ width: 160 }}
                    value={org.cnpj}
                    onChange={e => setO('cnpj', e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && buscarCnpj()}
                    placeholder="00.000.000/0001-00" />
                  <button className={s.btnLookup} onClick={buscarCnpj}
                    disabled={buscandoCnpj} title="Consultar na Receita Federal">
                    {buscandoCnpj ? '⏳' : '🔍'}
                  </button>
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>IE:</span>
                  <input className={s.inputErp} style={{ width: 160 }}
                    value={org.ie}
                    onChange={e => setO('ie', e.target.value)} />
                  <span style={{ fontSize: 12, marginLeft: 8, color: '#555' }}>IM:</span>
                  <input className={s.inputErp} style={{ width: 120 }}
                    value={org.im}
                    onChange={e => setO('im', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Regime Trib.:</span>
                  <select className={s.selectErp} style={{ width: 200 }}
                    value={org.regime_tributario}
                    onChange={e => setO('regime_tributario', e.target.value)}>
                    {REGIMES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                {String(org.regime_tributario || '').toLowerCase().includes('lucro') && (
                  <div className={s.checkRow} style={{ marginLeft: 4 }}>
                    <input type="checkbox" id="pis_cofins_excluir_icms"
                      checked={!!org.pis_cofins_excluir_icms}
                      onChange={e => setO('pis_cofins_excluir_icms', e.target.checked)} />
                    <label htmlFor="pis_cofins_excluir_icms"
                      title="Exclusão do ICMS da base do PIS/COFINS decidida pelo STF (Tema 69). Confirme com o contador antes de ligar.">
                      Tirar o ICMS da base do PIS/COFINS na nota (ex.: item R$ 100, ICMS R$ 18 → PIS/COFINS calculados sobre R$ 82) — confirmar com o contador
                    </label>
                  </div>
                )}
                <div className={s.campo}>
                  <span className={s.campoLabel}>Segmento:</span>
                  <select className={s.selectErp} style={{ width: 240 }}
                    value={org.segmento_id ?? ''}
                    onChange={e => setO('segmento_id', e.target.value ? parseInt(e.target.value) : null)}>
                    <option value="">— não informado —</option>
                    {segmentos.map(sg => <option key={sg.id} value={sg.id}>{sg.nome}</option>)}
                  </select>
                  <button type="button" className={s.btnErp} style={{ minWidth: 28, padding: '0 8px', marginLeft: 6 }}
                    title="Cadastrar novo segmento"
                    onClick={() => { setNovoSegmento(novoSegmento ? null : { nome: '', habilita_agendamento: false }); setErroSegmento(''); }}>
                    +
                  </button>
                  {segmentos.find(sg => sg.id === org.segmento_id)?.habilita_agendamento && (
                    <span style={{ fontSize: 11, color: '#0a7d2c', marginLeft: 8 }}>
                      ✓ libera o menu Agendamento
                    </span>
                  )}
                </div>

                {novoSegmento && (
                  <div className={s.campo} style={{ background: '#f5f8ff', padding: '8px 10px', borderRadius: 6, marginTop: 4 }}>
                    <span className={s.campoLabel}>Novo segmento:</span>
                    <input className={s.inputErp} style={{ width: 200 }} autoFocus
                      placeholder="Ex.: Mat. de Construção"
                      value={novoSegmento.nome}
                      onChange={e => setNovoSegmento(p => ({ ...p, nome: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && criarSegmento()} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, marginLeft: 10 }}>
                      <input type="checkbox" checked={novoSegmento.habilita_agendamento}
                        onChange={e => setNovoSegmento(p => ({ ...p, habilita_agendamento: e.target.checked }))} />
                      Libera Agendamento
                    </label>
                    <button type="button" className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ marginLeft: 10 }}
                      disabled={salvandoSegmento} onClick={criarSegmento}>
                      {salvandoSegmento ? 'Salvando...' : 'Adicionar'}
                    </button>
                    <button type="button" className={s.btnErp} style={{ marginLeft: 6 }}
                      onClick={() => setNovoSegmento(null)}>Cancelar</button>
                    {erroSegmento && <span style={{ color: '#c0392b', fontSize: 12, marginLeft: 10 }}>{erroSegmento}</span>}
                  </div>
                )}
              </fieldset>

              {/* CNAEs */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>CNAEs</legend>
                <p style={{ fontSize: 11, color: '#888', margin: '0 0 8px' }}>
                  Preenchidos automaticamente pela busca de CNPJ acima (🔍), ou cadastre manualmente.
                  Usados para sugerir serviços compatíveis no cadastro de produtos.
                </p>
                {cnaes.length > 0 && (
                  <table style={{ width: '100%', fontSize: 12, marginBottom: 8, borderCollapse: 'collapse' }}>
                    <tbody>
                      {cnaes.map(c => (
                        <tr key={c.codigo} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '3px 4px', width: 100, fontWeight: c.principal ? 700 : 400 }}>
                            {c.codigo}
                          </td>
                          <td style={{ padding: '3px 4px' }}>{c.descricao}</td>
                          <td style={{ padding: '3px 4px', width: 70, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {c.principal
                              ? <span style={{ color: '#2563eb', fontWeight: 700 }} title="CNAE principal">Principal</span>
                              : <button type="button" className={s.btnLookup}
                                  title="Marcar como principal"
                                  onClick={() => marcarPrincipal(c.codigo)}>☆</button>}
                            <button type="button" className={s.btnLookup} style={{ marginLeft: 4, color: '#b91c1c' }}
                              title="Remover" onClick={() => removerCnae(c.codigo)}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className={s.campo}>
                  <input className={s.inputErp} style={{ width: 110 }}
                    value={novoCnaeCodigo} placeholder="0000-0/00"
                    onChange={e => setNovoCnaeCodigo(e.target.value)} />
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={novoCnaeDescricao} placeholder="Descrição da atividade"
                    onChange={e => setNovoCnaeDescricao(e.target.value)} />
                  <button type="button" className={s.btnLookup}
                    onClick={() => { addCnae(novoCnaeCodigo, novoCnaeDescricao, cnaes.length === 0); setNovoCnaeCodigo(''); setNovoCnaeDescricao(''); }}>
                    + Adicionar
                  </button>
                </div>
              </fieldset>

              {/* Endereço */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Endereço</legend>
                <div className={s.campo}>
                  <span className={s.campoLabel}>CEP:</span>
                  <input className={s.inputErp} style={{ width: 100 }}
                    value={org.cep}
                    onChange={e => setO('cep', e.target.value)}
                    placeholder="00000-000" />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Logradouro:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={org.logradouro}
                    onChange={e => setO('logradouro', e.target.value)} />
                  <span style={{ fontSize: 12, marginLeft: 6, color: '#555' }}>Nº:</span>
                  <input className={s.inputErp} style={{ width: 70 }}
                    value={org.numero}
                    onChange={e => setO('numero', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Complemento:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={org.complemento}
                    onChange={e => setO('complemento', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Bairro:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={org.bairro}
                    onChange={e => setO('bairro', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Cidade:</span>
                  <input className={s.inputErp} style={{ width: 200 }}
                    value={org.cidade}
                    onChange={e => setO('cidade', e.target.value)} />
                  <span style={{ fontSize: 12, marginLeft: 6, color: '#555' }}>UF:</span>
                  <input className={s.inputErp} style={{ width: 50 }}
                    value={org.uf}
                    onChange={e => setO('uf', e.target.value.toUpperCase())}
                    maxLength={2} />
                </div>
              </fieldset>

              {/* Contato */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Contato</legend>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Telefone:</span>
                  <input className={s.inputErp} style={{ width: 160 }}
                    value={org.telefone}
                    onChange={e => setO('telefone', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>E-mail:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={org.email}
                    onChange={e => setO('email', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>Site:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    value={org.site}
                    onChange={e => setO('site', e.target.value)} />
                </div>
                <div className={s.campo}>
                  <span className={s.campoLabel}>E-mail do contador:</span>
                  <input className={s.inputErp} style={{ flex: 1 }}
                    placeholder="contador@escritorio.com.br"
                    value={org.email_contador}
                    onChange={e => setO('email_contador', e.target.value)} />
                </div>
              </fieldset>
            </div>

            {/* Coluna direita */}
            <div>
              {/* Ambiente NFe */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Ambiente</legend>
                <div className={s.radioRow}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="radio" name="ambiente" value="homologacao"
                      checked={org.ambiente_nfe === 'homologacao'}
                      onChange={() => setO('ambiente_nfe', 'homologacao')} />
                    Homologação (Testes)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="radio" name="ambiente" value="producao"
                      checked={org.ambiente_nfe === 'producao'}
                      onChange={() => setO('ambiente_nfe', 'producao')} />
                    Produção
                  </label>
                </div>
                {org.ambiente_nfe === 'producao' && (
                  <p style={{ color: '#cc0000', fontSize: 11, marginTop: 4 }}>
                    ⚠ Atenção: em produção as notas fiscais têm validade legal.
                  </p>
                )}
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 12 }}>Janela do DF-e (dias):</label>
                  <input type="number" min="1" max="90" className={s.input}
                    style={{ width: 70, textAlign: 'right' }}
                    value={org.dfe_dias_janela}
                    onChange={e => setO('dfe_dias_janela', e.target.value)} />
                  <span style={{ fontSize: 11, color: '#666' }}>
                    Notas emitidas há mais dias que isso não aparecem na importação (máx. 90 — limite da SEFAZ)
                  </span>
                </div>
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 12 }}>Prazo p/ cancelar NF-e (horas):</label>
                  <input type="number" min="0.5" max="720" step="0.5" className={s.input}
                    style={{ width: 70, textAlign: 'right' }}
                    value={org.prazo_cancel_nfe_horas}
                    onChange={e => setO('prazo_cancel_nfe_horas', e.target.value)} />
                  <label style={{ fontSize: 12 }}>NFC-e (minutos):</label>
                  <input type="number" min="1" max="4320" className={s.input}
                    style={{ width: 70, textAlign: 'right' }}
                    value={org.prazo_cancel_nfce_min}
                    onChange={e => setO('prazo_cancel_nfce_min', e.target.value)} />
                  <span style={{ fontSize: 11, color: '#666' }}>
                    Prazo legal da sua UF para cancelar na SEFAZ — passado ele, o sistema orienta a devolução
                  </span>
                </div>
              </fieldset>

              {/* Logo */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Logo</legend>
                {logoSrc && (
                  <div style={{ marginBottom: 8 }}>
                    <img src={logoSrc} alt="Logo"
                      style={{ maxHeight: 80, maxWidth: 220, border: '1px solid #ccc', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                <div className={s.campo}>
                  <span className={s.campoLabel}>Arquivo:</span>
                  <input type="file" accept="image/*" style={{ flex: 1 }}
                    onChange={e => { setLogoFile(e.target.files[0] || null); setLogoErro(''); }} />
                </div>
                {logoErro   && <p className={s.erroMsg}   style={{ margin: '4px 0' }}>{logoErro}</p>}
                {logoSucesso && <p className={s.sucessoMsg} style={{ margin: '4px 0' }}>{logoSucesso}</p>}
                <div style={{ marginTop: 6 }}>
                  <button className={s.btnErpPrimario}
                    onClick={uploadLogo}
                    disabled={uploadandoLogo || !logoFile}>
                    {uploadandoLogo ? 'Enviando...' : '⬆ Enviar Logo'}
                  </button>
                </div>
                <p style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                  Imagem JPG, PNG ou SVG (máx. 2 MB). Aparece no PDV, DANFE e impressões.
                </p>
              </fieldset>

              {/* CPF/CNPJ autorizados XML */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>CPF/CNPJ autorizados a obter XML</legend>
                <ul className={s.xmlList}>
                  {xmlList.map((x, i) => (
                    <li key={i} className={s.xmlItem}>
                      <span style={{ flex: 1, fontFamily: 'Courier New', fontSize: 12 }}>{x}</span>
                      <button className={s.btnErp} style={{ minWidth: 22, padding: '0 4px', height: 22 }}
                        onClick={() => removeXml(i)}>✕</button>
                    </li>
                  ))}
                </ul>
                <div className={s.xmlAddRow}>
                  <input className={s.inputErp} style={{ width: 200 }}
                    value={novoXml}
                    onChange={e => setNovoXml(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addXml()}
                    placeholder="000.000.000-00 ou 00.000.000/0001-00" />
                  <button className={s.btnErp} onClick={addXml}>+ Adicionar</button>
                </div>
              </fieldset>

              {/* Opções */}
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Opções</legend>
                <div className={s.checkRow}>
                  <input type="checkbox" id="preco_custo"
                    checked={org.habilitar_preco_custo}
                    onChange={e => setO('habilitar_preco_custo', e.target.checked)} />
                  <label htmlFor="preco_custo">Habilitar preço de custo/venda</label>
                </div>
                <div className={s.checkRow}>
                  <input type="checkbox" id="tls"
                    checked={org.habilitar_tls}
                    onChange={e => setO('habilitar_tls', e.target.checked)} />
                  <label htmlFor="tls">Habilitar TLS</label>
                </div>
                <div className={s.checkRow}>
                  <input type="checkbox" id="estoque_insuf"
                    checked={org.aceitar_estoque_insuficiente}
                    onChange={e => setO('aceitar_estoque_insuficiente', e.target.checked)} />
                  <label htmlFor="estoque_insuf">Aceitar estoque insuficiente</label>
                </div>
                <div className={s.campo} style={{ marginTop: 10 }}>
                  <span className={s.campoLabel}>Setor Padrão de Saída (PDV):</span>
                  <select className={s.inputErp}
                    value={org.setor_saida_id || ''}
                    onChange={e => setO('setor_saida_id', e.target.value || null)}>
                    <option value="">— nenhum —</option>
                    {setoresEstoque.map(se => (
                      <option key={se.id} value={se.id}>{se.sigla} — {se.nome}</option>
                    ))}
                  </select>
                </div>
              </fieldset>
            </div>
          </div>
        )}

        {/* ── ABA NFC-e ── */}
        {aba === 'nfce' && (
          <div>
            <fieldset className={s.fieldset} style={{ marginBottom: 10 }}>
              <legend className={s.legend}>NFC-e — Configurações</legend>

              <div className={s.campo}>
                <span className={s.campoLabel}>Id CSC:</span>
                <input className={s.inputErp} style={{ width: 100 }}
                  value={nfce.id_csc}
                  onChange={e => setN('id_csc', e.target.value)} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>CSC:</span>
                <input className={s.inputErp} style={{ width: 320 }}
                  value={nfce.csc}
                  onChange={e => setN('csc', e.target.value)} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>Ind. Presença:</span>
                <select className={s.selectErp} style={{ width: 340 }}
                  value={nfce.indicador_presenca}
                  onChange={e => setN('indicador_presenca', e.target.value)}>
                  {PRESENCAS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>Série:</span>
                <input className={s.inputErp} style={{ width: 80 }}
                  value={nfce.serie}
                  onChange={e => setN('serie', e.target.value)} />
              </div>

              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <span className={s.campoLabel} style={{ display: 'inline-block', minWidth: 120 }}>Tipo de Emissão:</span>
              </div>
              <div className={s.radioRow} style={{ marginLeft: 120 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="tipo_em_nfce" value="normal"
                    checked={nfce.tipo_emissao === 'normal'}
                    onChange={() => setN('tipo_emissao', 'normal')} />
                  Normal
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="tipo_em_nfce" value="contingencia"
                    checked={nfce.tipo_emissao === 'contingencia'}
                    onChange={() => setN('tipo_emissao', 'contingencia')} />
                  Em contingência
                </label>
              </div>

              {nfce.tipo_emissao === 'contingencia' && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ marginBottom: 3, fontSize: 12, fontWeight: 'bold' }}>Justificativa de Contingência:</div>
                  <textarea className={s.textareaAviso} rows={3}
                    value={nfce.justificativa_contingencia}
                    onChange={e => setN('justificativa_contingencia', e.target.value)} />
                </div>
              )}

              <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 3, fontSize: 12, fontWeight: 'bold' }}>Motivo de Cancelamento Padrão:</div>
                <textarea className={s.textareaAviso} rows={2}
                  value={nfce.motivo_cancelamento_padrao}
                  onChange={e => setN('motivo_cancelamento_padrao', e.target.value)} />
              </div>
            </fieldset>

            {/* Numeração NFC-e — só leitura: a série da organização (acima) mais
                qualquer série própria configurada num caixa (Financeiro →
                Contas → "Série NFC-e"). Não dá mais pra cadastrar série manual
                aqui — ela nasce ao configurar o caixa. */}
            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Numeração NFC-e</legend>
              <table className={s.numGrid}>
                <thead>
                  <tr>
                    <th>Série</th><th>Caixa</th><th>Próx. Número</th><th>Último NF</th>
                  </tr>
                </thead>
                <tbody>
                  {numeracaoCaixasNfce.length === 0 && (
                    <tr><td colSpan={4} style={{ color: '#888', fontSize: 12 }}>Nenhuma numeração encontrada.</td></tr>
                  )}
                  {numeracaoCaixasNfce.map((n, i) => (
                    <tr key={i}>
                      <td>{n.serie}</td>
                      <td>{n.caixa_nome || <span style={{ color: '#888' }}>— padrão da organização —</span>}</td>
                      <td>{n.proximo_numero ?? 1}</td>
                      <td>{n.ultimo_nf ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                Pra dar uma série própria a um caixa, configure em Financeiro → Contas Financeiras
                → editar o caixa → campo "Série NFC-e".
              </div>
            </fieldset>
          </div>
        )}

        {/* ── ABA NF-e ── */}
        {aba === 'nfe' && (
          <div>
            <fieldset className={s.fieldset} style={{ marginBottom: 10 }}>
              <legend className={s.legend}>NF-e — Configurações</legend>

              <div className={s.campo}>
                <span className={s.campoLabel}>Ind. Presença:</span>
                <select className={s.selectErp} style={{ width: 340 }}
                  value={nfe.indicador_presenca}
                  onChange={e => setE('indicador_presenca', e.target.value)}>
                  {PRESENCAS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>Ind. Finalização:</span>
                <select className={s.selectErp} style={{ width: 340 }}
                  value={nfe.indicador_finalizacao}
                  onChange={e => setE('indicador_finalizacao', e.target.value)}>
                  {FINALIZACOES.map(f => <option key={f.v} value={f.v}>{f.l}</option>)}
                </select>
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>Série:</span>
                <input className={s.inputErp} style={{ width: 80 }}
                  value={nfe.serie}
                  onChange={e => setE('serie', e.target.value)} />
              </div>

              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <span style={{ fontWeight: 'bold', fontSize: 12 }}>Tipo de Emissão:</span>
              </div>
              <div className={s.radioRow}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="tipo_em_nfe" value="normal"
                    checked={nfe.tipo_emissao === 'normal'}
                    onChange={() => setE('tipo_emissao', 'normal')} />
                  Normal
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="radio" name="tipo_em_nfe" value="contingencia"
                    checked={nfe.tipo_emissao === 'contingencia'}
                    onChange={() => setE('tipo_emissao', 'contingencia')} />
                  Em contingência
                </label>
              </div>

              {nfe.tipo_emissao === 'contingencia' && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ marginBottom: 3, fontSize: 12, fontWeight: 'bold' }}>Justificativa de Contingência:</div>
                  <textarea className={s.textareaAviso} rows={3}
                    value={nfe.justificativa_contingencia}
                    onChange={e => setE('justificativa_contingencia', e.target.value)} />
                </div>
              )}

              <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 3, fontSize: 12, fontWeight: 'bold' }}>Motivo de Cancelamento Padrão:</div>
                <textarea className={s.textareaAviso} rows={2}
                  value={nfe.motivo_cancelamento_padrao}
                  onChange={e => setE('motivo_cancelamento_padrao', e.target.value)} />
              </div>
            </fieldset>

            {/* Numeração NF-e */}
            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Numeração NF-e</legend>
              <table className={s.numGrid}>
                <thead>
                  <tr>
                    <th>Tipo</th><th>Série</th><th>Próx. Número</th><th>Último NF</th><th style={{width:40}}></th>
                  </tr>
                </thead>
                <tbody>
                  {numeracao.filter(n => n.tipo_doc === 'nfe').map((n) => {
                    const idx = numeracao.indexOf(n);
                    return (
                      <tr key={idx}>
                        <td>NF-e</td>
                        <td>
                          <input className={s.inputErp} style={{ width: 70 }}
                            value={n.serie}
                            onChange={e => setNumeracao(prev => prev.map((x, j) => j === idx ? { ...x, serie: e.target.value } : x))} />
                        </td>
                        <td>
                          <input className={s.inputErp} style={{ width: 90 }} type="number"
                            value={n.proximo_numero}
                            onChange={e => setNumeracao(prev => prev.map((x, j) => j === idx ? { ...x, proximo_numero: parseInt(e.target.value) || 1 } : x))} />
                        </td>
                        <td>{n.ultimo_nf ?? '—'}</td>
                        <td>
                          <button className={s.btnErp} style={{ minWidth: 24, padding: '0 4px' }}
                            onClick={() => removeNumeracao(idx)}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className={s.numAddRow}>
                <span style={{ fontSize: 12 }}>Série:</span>
                <input className={s.inputErp} style={{ width: 70 }}
                  value={novaNumNfe.serie}
                  onChange={e => setNovaNumNfe(p => ({ ...p, serie: e.target.value }))} />
                <span style={{ fontSize: 12 }}>Próx. Nº:</span>
                <input className={s.inputErp} style={{ width: 90 }} type="number"
                  value={novaNumNfe.proximo_numero}
                  onChange={e => setNovaNumNfe(p => ({ ...p, proximo_numero: e.target.value }))} />
                <button className={s.btnErp} onClick={() => addNumeracao('nfe', novaNumNfe, setNovaNumNfe)}>+ Série</button>
              </div>
            </fieldset>
          </div>
        )}

        {/* ── ABA NFS-e ── */}
        {/* Campos que já existem noutras abas (Ambiente, Inscrição Municipal,
            Endereço/Município, E-mail/Telefone, Certificado Digital) NÃO são
            repetidos aqui — a emissão de NFS-e reaproveita esses mesmos dados
            da aba Geral e da aba Certificado Digital. */}
        {aba === 'nfse' && (
          <div>
            <div style={{
              background: '#f5f8ff', border: '1px solid #dbe4f5', borderRadius: 6,
              padding: '8px 12px', fontSize: 12, color: '#444', marginBottom: 10,
            }}>
              ℹ️ Ambiente, Inscrição Municipal (IM), Endereço/Município e E-mail/Telefone da
              NFS-e usam os mesmos dados já configurados na aba <strong>Geral</strong>. O
              certificado digital usado pra assinar a DPS é o mesmo da aba{' '}
              <strong>Certificado Digital</strong>.
            </div>

            <fieldset className={s.fieldset} style={{ marginBottom: 10 }}>
              <legend className={s.legend}>NFS-e — Sequenciamento</legend>
              <div className={s.campo}>
                <span className={s.campoLabel}>Série DPS:</span>
                <input className={s.inputErp} style={{ width: 80 }}
                  value={org.nfse_serie}
                  onChange={e => setO('nfse_serie', e.target.value)} />
                <span style={{ fontSize: 11, color: '#888', marginLeft: 10 }}>
                  Vai dentro do identificador da DPS (nDPS) — não é o número que aparece na
                  nota (esse é atribuído pelo Sistema Nacional na hora da emissão).
                </span>
              </div>
              {/* Último numeroNfse (o número REAL da nota, atribuído pelo Sistema
                  Nacional) — espelhado sozinho a cada emissão autorizada por AQUI
                  (organizacao_numeracao tipo_doc='nfse', não 'nfse_dps' — números
                  desacoplados, ver comentário acima). Só reflete emissões feitas
                  por este sistema; se a última nota foi emitida por outra
                  ferramenta (troca de sistema, por exemplo), não tem como esta
                  tela saber sozinha — não existe uma API do Sistema Nacional pra
                  "qual foi a última nota deste CNPJ", só consulta por chave já
                  conhecida. Fica em branco até a 1ª emissão autorizada por aqui. */}
              <div className={s.campo} style={{ marginTop: 4 }}>
                <span className={s.campoLabel}>Última NFS-e emitida (por aqui):</span>
                <span style={{ fontWeight: 600 }}>
                  {(() => {
                    const linha = numeracao.find(n => n.tipo_doc === 'nfse' && String(n.serie) === String(org.nfse_serie));
                    return linha?.ultimo_nf ?? '— (nenhuma emitida por este sistema ainda)';
                  })()}
                </span>
              </div>
            </fieldset>

            {/* Numeração DPS — mesmo componente/tabela genérico já usado em NF-e,
                só filtrando tipo_doc='nfse_dps' (número interno da DPS; DIFERENTE
                do número da NFS-e emitida, que o Sistema Nacional atribui). */}
            <fieldset className={s.fieldset} style={{ marginBottom: 10 }}>
              <legend className={s.legend}>Numeração DPS</legend>
              <table className={s.numGrid}>
                <thead>
                  <tr>
                    <th>Tipo</th><th>Série</th><th>Próx. Nº DPS</th><th>Último</th><th style={{width:40}}></th>
                  </tr>
                </thead>
                <tbody>
                  {numeracao.filter(n => n.tipo_doc === 'nfse_dps').map((n) => {
                    const idx = numeracao.indexOf(n);
                    return (
                      <tr key={idx}>
                        <td>DPS</td>
                        <td>
                          <input className={s.inputErp} style={{ width: 70 }}
                            value={n.serie}
                            onChange={e => setNumeracao(prev => prev.map((x, j) => j === idx ? { ...x, serie: e.target.value } : x))} />
                        </td>
                        <td>
                          <input className={s.inputErp} style={{ width: 90 }} type="number"
                            value={n.proximo_numero}
                            onChange={e => setNumeracao(prev => prev.map((x, j) => j === idx ? { ...x, proximo_numero: parseInt(e.target.value) || 1 } : x))} />
                        </td>
                        <td>{n.ultimo_nf ?? '—'}</td>
                        <td>
                          <button className={s.btnErp} style={{ minWidth: 24, padding: '0 4px' }}
                            onClick={() => removeNumeracao(idx)}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className={s.numAddRow}>
                <span style={{ fontSize: 12 }}>Série:</span>
                <input className={s.inputErp} style={{ width: 70 }}
                  value={novaNumNfse.serie}
                  onChange={e => setNovaNumNfse(p => ({ ...p, serie: e.target.value }))} />
                <span style={{ fontSize: 12 }}>Próx. Nº DPS:</span>
                <input className={s.inputErp} style={{ width: 90 }} type="number"
                  value={novaNumNfse.proximo_numero}
                  onChange={e => setNovaNumNfse(p => ({ ...p, proximo_numero: e.target.value }))} />
                <button className={s.btnErp} onClick={() => addNumeracao('nfse_dps', novaNumNfse, setNovaNumNfse)}>+ Série</button>
              </div>
            </fieldset>

            <fieldset className={s.fieldset}>
              <legend className={s.legend}>Valor Aproximado dos Tributos</legend>
              <div className={s.campo}>
                <span className={s.campoLabel}>Alíquota do Simples Nacional (%):</span>
                <input className={s.inputErp} style={{ width: 100 }} type="number" step="0.01" min="0" max="100"
                  value={org.nfse_percentual_tributos_sn}
                  onChange={e => setO('nfse_percentual_tributos_sn', e.target.value)}
                  placeholder="ex.: 6,00" />
              </div>
              <p style={{ fontSize: 11, color: '#888', margin: '6px 0 0' }}>
                Percentual total de tributos do Simples Nacional (depende da faixa de receita
                bruta dos últimos 12 meses e do anexo do seu Simples — confira com o contador).
                Vai na DPS como "Totais aproximados dos Tributos". Deixe em branco pra não
                informar (a DPS ainda assim é aceita, só sem esse dado no DANFSe).
              </p>
            </fieldset>

            <fieldset className={s.fieldset}>
              <legend className={s.legend}>ISS Retido pelo Tomador</legend>
              <div className={s.campo}>
                <span className={s.campoLabel}>Alíquota do ISS retido (%):</span>
                <input className={s.inputErp} style={{ width: 100 }} type="number" step="0.01" min="0" max="5"
                  value={org.nfse_aliquota_iss_retido}
                  onChange={e => setO('nfse_aliquota_iss_retido', e.target.value)}
                  placeholder="ex.: 3,21" />
              </div>
              <p style={{ fontSize: 11, color: '#888', margin: '6px 0 0' }}>
                Usada só na NFS-e de cliente marcado com "Retém ISS na NFS-e" (Cadastro de Clientes).
                Ex.: serviço de R$ 1.000,00 com 3,21% → o tomador retém R$ 32,10 de ISS e paga
                R$ 967,90. No Simples Nacional é a alíquota efetiva do ISS na sua faixa — muda
                com a receita dos últimos 12 meses, confirme com o contador.
              </p>
            </fieldset>

            {(String(id || '') === String(usuario?.organizacao_id || '')) && (
              <fieldset className={s.fieldset}>
                <legend className={s.legend}>Retenção inteligente do ISSQN</legend>
                <ConfigRetencaoIss />
              </fieldset>
            )}
          </div>
        )}

        {/* ── ABA MDF-e ── */}
        {aba === 'mdfe' && (
          String(id || '') === String(usuario?.organizacao_id || '') || !isEdicao
            ? <ConfigMdfe />
            : (
              <div style={{ padding: 20, color: '#666', textAlign: 'center', fontSize: 13 }}>
                A configuração do MDF-e vale para a empresa em que você está logado.
                Entre nesta empresa (troca de empresa no topo) e abra Minha Empresa → MDF-e.
              </div>
            )
        )}

        {/* ── ABA CERTIFICADO ── */}
        {/* ── ABA PARÂMETROS ── */}
        {aba === 'parametros' && (
          <div>
            {/* Sub-abas */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, borderBottom: '2px solid #c8d6e0', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 2 }}>
                {[['rotinas', 'Rotinas'], ['pdv', 'PDV'], ['vendas', 'Vendas'], ['horarios', 'Horários'],
                  ...(isEdicao && usuario?.suporte_sistema ? [['modulos', '🔒 Módulos']] : [])].map(([k, l]) => (
                  <button key={k}
                    onClick={() => setSubAbaParam(k)}
                    style={{
                      padding: '5px 16px', fontSize: 13, cursor: 'pointer', border: 'none',
                      borderBottom: subAbaParam === k ? '2px solid #2563eb' : '2px solid transparent',
                      background: 'none', color: subAbaParam === k ? '#2563eb' : '#555',
                      fontWeight: subAbaParam === k ? 'bold' : 'normal', marginBottom: -2,
                    }}>{l}</button>
                ))}
              </div>
              {isEdicao && isAdmin && (
                <button
                  type="button"
                  onClick={abrirModalCopiarConfig}
                  title="Copia Rotinas/PDV/Vendas/Horários e Bandeiras de cartão de outra organização (substitui tudo), e acrescenta ao Plano de Contas as contas que faltarem — pensado pra configurar rápido uma loja nova a partir de uma já pronta."
                  style={{
                    marginBottom: 8, padding: '6px 14px', fontSize: 12.5, cursor: 'pointer',
                    border: '1px solid #93c5fd', borderRadius: 6, background: '#eff6ff', color: '#1e40af',
                  }}
                >📋 Copiar Configurações de Organização…</button>
              )}
            </div>

            {subAbaParam === 'modulos' && isEdicao && usuario?.suporte_sistema && (
              <ModulosLiberados orgId={id} />
            )}

            {subAbaParam === 'horarios' && (
              <div style={{ maxWidth: 700 }}>
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <strong style={{ fontSize: 14, color: '#1e40af', display: 'block', marginBottom: 6 }}>
                    Horário de Funcionamento
                  </strong>
                  <p style={{ margin: '0 0 14px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                    Usado pela tela de <strong>Agendamento</strong> pra só oferecer horários dentro do
                    expediente — dia sem nenhum intervalo fica marcado como fechado. Enquanto não
                    configurar aqui, a Agenda não bloqueia nenhum horário por dia da semana.
                  </p>
                  {[['dom', 'Domingo'], ['seg', 'Segunda'], ['ter', 'Terça'], ['qua', 'Quarta'],
                    ['qui', 'Quinta'], ['sex', 'Sexta'], ['sab', 'Sábado']].map(([dia, label]) => {
                    const intervalos = org.horario_funcionamento?.[dia] || [];
                    const setIntervalos = novos => setO('horario_funcionamento', { ...org.horario_funcionamento, [dia]: novos });
                    return (
                      <div key={dia} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0',
                        borderBottom: '1px solid #eef2f7',
                      }}>
                        <span style={{ width: 90, fontSize: 13, fontWeight: 600, paddingTop: 6 }}>{label}</span>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {intervalos.length === 0 && (
                            <span style={{ fontSize: 12, color: '#999', paddingTop: 6 }}>Fechado</span>
                          )}
                          {intervalos.map(([ini, fim], idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input type="time" className={s.inputErp} style={{ width: 100 }}
                                value={ini}
                                onChange={e => setIntervalos(intervalos.map((it, i) => i === idx ? [e.target.value, it[1]] : it))} />
                              <span style={{ fontSize: 12, color: '#888' }}>às</span>
                              <input type="time" className={s.inputErp} style={{ width: 100 }}
                                value={fim}
                                onChange={e => setIntervalos(intervalos.map((it, i) => i === idx ? [it[0], e.target.value] : it))} />
                              <button type="button" className={s.btnLookup}
                                onClick={() => setIntervalos(intervalos.filter((_, i) => i !== idx))}
                                title="Remover este intervalo">✕</button>
                            </div>
                          ))}
                          <button type="button" className={s.btnLookup} style={{ width: 'fit-content', fontSize: 11 }}
                            onClick={() => setIntervalos([...intervalos, ['08:00', '18:00']])}>
                            + {intervalos.length === 0 ? 'Abrir neste dia' : 'Adicionar intervalo'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {subAbaParam === 'vendas' && (
              <div style={{ maxWidth: 700 }}>
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox"
                      style={{ marginTop: 3 }}
                      checked={org.finalizar_venda_pedido}
                      onChange={e => setO('finalizar_venda_pedido', e.target.checked)} />
                    <span>
                      <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                        Finalizar vendas pela tela de Pedidos/Orçamentos
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.6, display: 'block' }}>
                        Marcado: o pedido aprovado pode ser convertido em venda finalizada na própria tela
                        de Pedidos/Orçamentos, com emissão de NF-e/NFC-e e impressões ali mesmo — e a tela
                        de Devoluções também emite a NF-e de devolução diretamente.
                        <br />
                        Desmarcado: a finalização acontece no PDV — o F2 (Pré-venda) puxa os pedidos
                        aprovados e as devoluções para finalizar no caixa.
                      </span>
                    </span>
                  </label>
                </div>

                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 14,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox"
                      style={{ marginTop: 3 }}
                      checked={!!org.recorrencia_habilitada}
                      onChange={e => setO('recorrencia_habilitada', e.target.checked)} />
                    <span>
                      <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                        Habilitar recorrência
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.6, display: 'block' }}>
                        Marcado: aparece a aba <strong>Recorrência</strong> no Cadastro de Clientes (onde cada cliente
                        recebe o serviço, valor, dia de vencimento, banco do boleto e se gera boleto/NFS-e) e o menu
                        <strong> Vendas → Gerar Recorrência</strong>, que gera de uma vez as vendas do mês já
                        finalizadas em boleto, com NFS-e e e-mail conforme marcado.
                        <br />
                        <strong>Exemplo:</strong> cliente com mensalidade de R$ 150,00 todo dia 10 → em
                        "Gerar Recorrência" de novembro sai a venda com boleto vencendo em 10/11.
                      </span>
                    </span>
                  </label>
                </div>

                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 14,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox"
                      style={{ marginTop: 3 }}
                      checked={org.solicitar_vendedor_pedido}
                      onChange={e => setO('solicitar_vendedor_pedido', e.target.checked)} />
                    <span>
                      <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                        Solicitar vendedor ao iniciar venda/pedido/orçamento (Novo — F4)
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.6, display: 'block' }}>
                        Marcado: ao clicar em "Novo (F4)" em Pedidos/Orçamentos, o cursor vai direto pro
                        campo Vendedor — digite o código (id do usuário) ou o nome e confirme; o vendedor
                        resolvido fica gravado no pedido e segue pra venda quando ela for finalizada,
                        aparecendo no rodapé da NF-e/NFC-e, no próprio pedido/orçamento/venda e nos
                        relatórios de comissão. Sem um vendedor válido escolhido, não é possível salvar.
                      </span>
                    </span>
                  </label>
                </div>

                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 14,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox"
                      style={{ marginTop: 3 }}
                      checked={proposta.ativa}
                      onChange={e => ligarProposta(e.target.checked)} />
                    <span>
                      <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                        Trabalhar com Proposta Comercial em Pedidos/Orçamentos
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.6, display: 'block' }}>
                        Marcado: a tela de Pedidos/Orçamentos ganha a aba "Proposta" (equipamento, nota de
                        entrada, A/C, garantia, local do serviço) e o botão "Proposta", que imprime a proposta
                        comercial com cliente, serviços, peças e as condições abaixo.
                        <br />
                        Desmarcado: nada disso aparece. Os dados já lançados ficam guardados.
                      </span>
                    </span>
                  </label>

                  {proposta.ativa && (
                    <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', gap: 10 }}>
                        {[
                          ['garantia_meses', 'Garantia padrão (meses)', '3'],
                          ['validade_dias', 'Validade padrão (dias)', '15'],
                          ['impostos', 'Impostos', 'Inclusos'],
                          ['local_servico', 'Local de realização dos serviços', 'Ex.: Nossa oficina'],
                        ].map(([campo, rotulo, ph]) => (
                          <label key={campo} style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {rotulo}
                            <input className={s.inputErp} value={proposta.config[campo] || ''} placeholder={ph}
                              onChange={e => setPropCfg(campo, e.target.value)} />
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>
                        Garantia, impostos e local podem ser trocados em cada orçamento. A validade usa a data
                        de validade do orçamento quando ela estiver preenchida.
                      </div>
                      {[
                        ['texto_introducao', 'Texto de abertura (depois de "Prezado(a),")', 3],
                        ['texto_responsabilidades', 'Cláusula "Responsabilidades do Cliente" — uma linha por item', 3],
                        ['texto_garantia', 'Cláusula "Garantia" — uma linha por item', 6],
                        ['texto_obs_final', 'Observação final (em negrito, no fim da proposta)', 3],
                      ].map(([campo, rotulo, linhas]) => (
                        <label key={campo} style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {rotulo}
                          <textarea className={s.inputErp} rows={linhas} style={{ height: 'auto', resize: 'vertical', fontFamily: 'inherit' }}
                            value={proposta.config[campo] || ''}
                            onChange={e => setPropCfg(campo, e.target.value)} />
                        </label>
                      ))}
                      <div style={{ fontSize: 11, color: '#666' }}>Cláusula em branco não sai na proposta.</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        {[['cabecalho', 'imagem_cabecalho_url', 'Imagem de cabeçalho (opcional)'],
                          ['rodape', 'imagem_rodape_url', 'Imagem de rodapé (opcional)']].map(([campo, chave, rotulo]) => (
                          <div key={campo} style={{ fontSize: 12 }}>
                            <div style={{ marginBottom: 4 }}>{rotulo}</div>
                            {proposta.config[chave] ? (
                              <div>
                                <img alt={rotulo} style={{ maxWidth: '100%', maxHeight: 70, border: '1px solid #ddd', display: 'block', marginBottom: 4 }}
                                  src={proposta.config[chave].startsWith('http') ? proposta.config[chave] : `${BACKEND_URL}${proposta.config[chave]}`} />
                                <button type="button" className={s.btnErp} onClick={() => setPropCfg(chave, '')}>Remover</button>
                              </div>
                            ) : (
                              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                                disabled={propostaImgEnviando === campo}
                                onChange={e => enviarImagemProposta(campo, e.target.files?.[0])} />
                            )}
                            {propostaImgEnviando === campo && <span> Enviando...</span>}
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>
                        Sem imagem de cabeçalho, sai o logo e os dados da empresa. Sem imagem de rodapé, sai
                        endereço, telefone e CNPJ. Use imagens largas (ex.: 1800×250 px). Clique em Salvar depois de
                        trocar as imagens ou os textos.
                      </div>
                    </div>
                  )}
                </div>

                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 14,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox"
                      style={{ marginTop: 3 }}
                      checked={org.caixa_compartilhado}
                      onChange={e => setO('caixa_compartilhado', e.target.checked)} />
                    <span>
                      <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                        Caixa compartilhado entre operadores
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.6, display: 'block' }}>
                        Marcado: o turno de caixa passa a ser da CONTA financeira, não do usuário logado —
                        se um vendedor já abriu/está usando o caixa com um login (ex.: um login genérico de
                        vendas) e outro usuário (ex.: o gerente) loga com a própria conta pra abrir/fechar
                        caixa ou lançar uma entrada/saída avulsa, ambos caem no MESMO turno em vez de abrir
                        um turno paralelo. Fechar o turno de outro operador passa a exigir perfil admin ou
                        gerente (o próprio dono sempre pode fechar o que abriu). Útil quando várias pessoas
                        seguidas operam o mesmo caixa físico com logins diferentes ao longo do dia.
                        <br />
                        Desmarcado (padrão): cada usuário tem seu próprio turno, como sempre foi.
                      </span>
                    </span>
                  </label>
                </div>

                {/* ── Simulação de Preço (precificação) ── */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 14,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <strong style={{ fontSize: 14, color: '#1e40af', display: 'block', marginBottom: 6 }}>
                    Simulação de Preço (precificação de produtos)
                  </strong>
                  <p style={{ margin: '0 0 10px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                    Método usado na formação do preço de venda. Os índices (%) ficam no cadastro de
                    <strong> Tipos de Movimento</strong>; o regime tributário (aba Geral:
                    <strong> {org.regime_tributario || 'Simples Nacional'}</strong>) define se vale o
                    % do Simples Nacional ou o ICMS efetivo do estado.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 12, fontWeight: 600 }}>Método:</label>
                    <select className={s.input} style={{ width: 340 }}
                      value={org.simulacao_preco || 'markup_divisor'}
                      onChange={e => setO('simulacao_preco', e.target.value)}>
                      <option value="markup_divisor">Markup divisor (taxa de marcação — % sobre a venda)</option>
                      <option value="markup_comum">Markup comum (multiplicador — % sobre o custo)</option>
                    </select>
                  </div>
                  <p style={{ margin: '8px 0 0', fontSize: 11, color: '#666' }}>
                    Divisor: preço = custo ÷ (1 − Σ%); garante a margem líquida depois dos impostos
                    e despesas "por dentro". Comum: preço = custo × (1 + Σ%).
                  </p>
                </div>
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 12,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox"
                      style={{ marginTop: 3 }}
                      checked={!!org.habilitar_servicos}
                      onChange={e => setO('habilitar_servicos', e.target.checked)} />
                    <span>
                      <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                        Habilitar Serviços
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.6, display: 'block' }}>
                        Marcado: produtos cadastrados com Tipo = "serviço" (aba Geral do Cadastro de
                        Produtos) podem ser vinculados a um item da Lista de Serviços (LC 116/2003); em
                        Pedidos/Orçamentos, a tela e as impressões passam a agrupar Produtos e Serviços
                        separadamente, cada um com seu subtotal, além do total geral. NF-e/NFC-e continuam
                        emitidas normalmente só para mercadoria — emissão de NFS-e não faz parte disso ainda.
                      </span>
                    </span>
                  </label>
                </div>
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 12,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                    Conta bancária padrão para boletos
                  </strong>
                  <span style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 8 }}>
                    Usada quando a venda é finalizada com forma de pagamento <strong>Boleto</strong> e
                    nenhum banco foi escolhido na tela. Com mais de um banco configurado, o PDV e a tela
                    de Pedidos pedem a escolha na hora.
                  </span>
                  <SelectContaBoleto value={org.boleto_conta_id}
                    onChange={v => setO('boleto_conta_id', v)} />
                </div>
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginTop: 12,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                    Multa e juros por atraso (Contas a Receber)
                  </strong>
                  <p style={{ margin: '0 0 10px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                    Aplicados automaticamente na tela de baixa de um título vencido (boleto, cheque,
                    crediário...) — o operador vê o valor já calculado e pode ajustar, mas alterar exige
                    senha de administrador ou gerente. Sem atraso além da carência, nada é cobrado.
                  </p>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, fontWeight: 600 }}>
                      Multa (%)
                      <input type="number" step="0.01" min="0" className={s.input} style={{ width: 120 }}
                        value={org.multa_atraso_pct}
                        onChange={e => setO('multa_atraso_pct', e.target.value)} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, fontWeight: 600 }}>
                      Juros ao mês (%)
                      <input type="number" step="0.01" min="0" className={s.input} style={{ width: 120 }}
                        value={org.juros_atraso_pct_mes}
                        onChange={e => setO('juros_atraso_pct_mes', e.target.value)} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, fontWeight: 600 }}>
                      Carência (dias)
                      <input type="number" step="1" min="0" className={s.input} style={{ width: 120 }}
                        value={org.carencia_atraso_dias}
                        onChange={e => setO('carencia_atraso_dias', e.target.value)} />
                    </label>
                  </div>
                </div>
                <p style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
                  A alteração é salva junto com os demais dados da organização (botão Salvar).
                </p>
              </div>
            )}

            {subAbaParam === 'pdv' && (
              <div style={{ maxWidth: 700 }}>
                {/* Comportamento do lançamento de itens */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginBottom: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
                    Comportamento do lançamento de itens
                  </strong>
                  {PDV_CONTROLES.filter(ctrl => !ctrl.pai || org.pdv_config?.[ctrl.pai]).map(ctrl => (
                    <label key={ctrl.key}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12,
                        marginLeft: ctrl.pai ? 28 : 0 }}>
                      <input type="checkbox"
                        style={{ marginTop: 3 }}
                        checked={!!org.pdv_config?.[ctrl.key]}
                        onChange={e => setO('pdv_config', { ...org.pdv_config, [ctrl.key]: e.target.checked })} />
                      <span>
                        <strong style={{ fontSize: 13, display: 'block', marginBottom: 3 }}>{ctrl.titulo}</strong>
                        <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block' }}>{ctrl.desc}</span>
                      </span>
                    </label>
                  ))}

                  {org.pdv_config?.taxa_entrega_habilitada && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, maxWidth: 420, marginLeft: 28, marginBottom: 12 }}>
                      <strong style={{ fontSize: 13 }}>Taxa de entrega padrão (R$)</strong>
                      <input type="number" min="0" step="0.01" style={{ width: 100, padding: '6px 8px' }}
                        placeholder="opcional"
                        value={org.pdv_config?.taxa_entrega_padrao ?? ''}
                        onChange={e => setO('pdv_config', { ...org.pdv_config, taxa_entrega_padrao: e.target.value })} />
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5 }}>
                        Só uma sugestão: nunca entra sozinha na venda. <strong>Exemplo:</strong> com 5,00
                        informado, o botão "+ Taxa de entrega" da comanda já abre com 5,00 e o F9 mostra
                        o atalho "Padrão 5,00"; o operador confirma ou troca o valor em cada venda.
                      </span>
                    </label>
                  )}

                  <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 6, paddingTop: 14 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, maxWidth: 420 }}>
                      <strong style={{ fontSize: 13 }}>Prazo pra excluir item sem senha (segundos)</strong>
                      <input type="number" min="0" max="3600" style={{ width: 100, padding: '6px 8px' }}
                        placeholder="0 = nunca pede senha"
                        value={org.pdv_config?.tempo_senha_exclusao_item ?? ''}
                        onChange={e => setO('pdv_config', { ...org.pdv_config, tempo_senha_exclusao_item: e.target.value })} />
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5 }}>
                        Contado a partir do momento em que o item foi lançado (no PDV ou no F1 —
                        Mesas/Comandas). Dentro desse prazo, o operador exclui livremente (corrigir um
                        erro na hora). Depois do prazo, excluir passa a exigir senha de administrador
                        ou gerente. <strong>Exemplo:</strong> com 30 informado, um item lançado há 45s
                        já pede senha pra ser excluído; um lançado há 10s ainda não pede. Deixe em
                        branco ou 0 pra nunca pedir senha (como é hoje).
                      </span>
                    </label>
                  </div>
                </div>

                {/* Etiqueta impressa pela balança — lida no PDV (utils/etiquetaBalanca.js) */}
                {(() => {
                  const et = configEtiqueta(org.pdv_config);
                  const setEt = (campo, valor) =>
                    setO('pdv_config', { ...org.pdv_config, balanca_etiqueta: { ...et, [campo]: valor } });
                  const exemploCod = '30869'.padStart(et.tamanho, '0').slice(-et.tamanho);
                  return (
                    <div style={{
                      border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginBottom: 20,
                      background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                    }}>
                      <strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
                        Etiqueta de balança
                      </strong>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                        <input type="checkbox" style={{ marginTop: 3 }}
                          checked={et.ativo}
                          onChange={e => setEt('ativo', e.target.checked)} />
                        <span>
                          <strong style={{ fontSize: 13, display: 'block', marginBottom: 3 }}>
                            Reconhecer no PDV a etiqueta impressa pela balança
                          </strong>
                          <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block' }}>
                            Ao bipar um código de 13 dígitos começando com 2, o PDV procura o produto marcado
                            como <strong>Produto de balança</strong> (Cad. Produtos → Atributos) e lança com a
                            quantidade/valor da etiqueta. Os mesmos produtos são os que vão no arquivo de
                            Exportar para Balança (tela Produtos). Precisa bater com a configuração da balança.
                          </span>
                        </span>
                      </label>
                      {et.ativo && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 12.5, marginLeft: 28 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <strong>Usar</strong>
                            <select value={et.usar} onChange={e => setEt('usar', e.target.value)} style={{ padding: '5px 6px' }}>
                              <option value="codigo">Código do produto</option>
                              <option value="barras">Código de barras</option>
                            </select>
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <strong>Informar</strong>
                            <select value={et.informar} onChange={e => setEt('informar', e.target.value)} style={{ padding: '5px 6px' }}>
                              <option value="valor">Valor (R$)</option>
                              <option value="peso">Peso (kg)</option>
                            </select>
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <strong>Casas decimais</strong>
                            <input type="number" min="0" max="3" style={{ width: 70, padding: '5px 6px' }}
                              value={et.decimais} onChange={e => setEt('decimais', e.target.value)} />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <strong>Iniciar no dígito</strong>
                            <input type="number" min="2" max="7" style={{ width: 70, padding: '5px 6px' }}
                              value={et.inicio} onChange={e => setEt('inicio', e.target.value)} />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <strong>Tamanho do código</strong>
                            <select value={et.tamanho} onChange={e => setEt('tamanho', e.target.value)} style={{ padding: '5px 6px' }}>
                              {[4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </label>
                          <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, flexBasis: '100%' }}>
                            <strong>Exemplo:</strong> com esta configuração, a etiqueta{' '}
                            <code>{'2'.padEnd(et.inicio - 1, '0')}{exemploCod}…</code> é lida como o produto de
                            código <strong>{Number(exemploCod)}</strong>, e os dígitos seguintes (até o último, que é
                            o verificador) como o <strong>{et.informar === 'peso' ? 'peso' : 'valor'}</strong> com{' '}
                            {et.decimais} casa(s) decimal(is). Ex.: 2 30869 0 00464 2 = produto 30869, {et.informar === 'peso' ? '0,464 kg (com 3 casas)' : 'R$ 4,64'}.
                          </span>
                        </div>
                      )}
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, marginTop: 14, maxWidth: 460 }}>
                        <strong style={{ fontSize: 13 }}>Pasta do arquivo de carga da balança</strong>
                        <input type="text" style={{ width: 220, padding: '6px 8px' }} placeholder="C:\Filizola"
                          value={org.pdv_config?.balanca_pasta_carga ?? ''}
                          onChange={e => setO('pdv_config', { ...org.pdv_config, balanca_pasta_carga: e.target.value })} />
                        <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5 }}>
                          Pasta que o programa da balança lê (Produtos → Exportar p/ Balança). <strong>Exemplo:</strong>{' '}
                          C:\E+. Na 1ª exportação de cada computador o navegador pede pra escolher essa pasta uma vez;
                          depois o arquivo é gravado direto nela (Edge/Chrome). Em branco = C:\Filizola.
                        </span>
                      </label>
                    </div>
                  );
                })()}

                {/* Sangria/Suprimento automático pelo caixa do PDV */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginBottom: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
                    Sangria e Suprimento pelo PDV
                  </strong>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                    <input type="checkbox" style={{ marginTop: 3 }}
                      checked={!!org.pdv_config?.sangria_suprimento_auto}
                      onChange={e => setO('pdv_config', { ...org.pdv_config, sangria_suprimento_auto: e.target.checked })} />
                    <span>
                      <strong style={{ fontSize: 13, display: 'block', marginBottom: 3 }}>
                        Realizar sangria/suprimentos pelo PDV
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block' }}>
                        Desligado (padrão): abrir/fechar turno só grava o saldo de abertura/fechamento no
                        turno, sem gerar movimentação financeira. Ligado: ao ABRIR o caixa, o valor
                        informado gera um <strong>suprimento de verdade</strong> (débito na conta escolhida
                        abaixo, crédito no caixa aberto) — aparece no Movimento do turno e no extrato da
                        conta, igual a um suprimento lançado manualmente. Ao FECHAR o caixa, o valor
                        informado no fechamento gera uma <strong>sangria</strong> (débito no caixa, crédito
                        na mesma conta). Alterar este parâmetro só vale a partir de agora — turnos já
                        abertos/fechados antes não são afetados.
                      </span>
                    </span>
                  </label>
                  {org.pdv_config?.sangria_suprimento_auto && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, maxWidth: 420 }}>
                      <strong style={{ fontSize: 13 }}>Conta de débito/crédito do suprimento/sangria</strong>
                      <SelectContaFinanceira
                        value={org.pdv_config?.sangria_suprimento_conta_id}
                        onChange={v => setO('pdv_config', { ...org.pdv_config, sangria_suprimento_conta_id: v || null })} />
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5 }}>
                        No suprimento (abertura), esta conta é <strong>debitada</strong> e o caixa aberto é
                        creditado. Na sangria (fechamento), o caixa é <strong>debitado</strong> e esta conta
                        é creditada. Sem uma conta escolhida aqui, nenhuma movimentação automática é gerada
                        mesmo com o parâmetro ligado.
                      </span>
                    </label>
                  )}
                </div>

                {/* Fechamento automático de cartão — alternativa à conciliação manual
                    pela tela Baixa de Cartões (Parâmetros → PDV). */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginBottom: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
                    Fechamento de Cartão
                  </strong>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
                    <input type="checkbox" style={{ marginTop: 3 }}
                      checked={!!org.cartao_config?.baixa_automatica}
                      onChange={e => setO('cartao_config', { ...org.cartao_config, baixa_automatica: e.target.checked })} />
                    <span>
                      <strong style={{ fontSize: 13, display: 'block', marginBottom: 3 }}>
                        Fechar cartão automaticamente, sem conciliar em lote
                      </strong>
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block' }}>
                        Desligado (padrão): toda venda no cartão continua caindo em aberto, esperando
                        a conferência manual na tela <strong>Baixa de Cartões</strong> — use quando quiser
                        conciliar contra o repasse real da operadora. Ligado: a própria venda já fecha o
                        recebimento na hora, assumindo um prazo fixo — <strong>débito</strong> no próximo
                        dia útil (só pula sábado/domingo, sem feriados), <strong>crédito à vista</strong> em
                        30 dias e <strong>crédito parcelado</strong> a cada 30 dias por parcela. A taxa da
                        bandeira continua sendo descontada, e a baixa aparece no Histórico da Baixa de
                        Cartões marcada como automática (pode ser estornada do mesmo jeito).
                      </span>
                    </span>
                  </label>
                  {org.cartao_config?.baixa_automatica && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, maxWidth: 420 }}>
                      <strong style={{ fontSize: 13 }}>Conta controle do fechamento automático</strong>
                      <SelectContaFinanceira
                        value={org.cartao_config?.conta_controle_id}
                        onChange={v => setO('cartao_config', { ...org.cartao_config, conta_controle_id: v || null })} />
                      <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5 }}>
                        Recebe o valor líquido (já descontada a taxa) de todo cartão fechado
                        automaticamente. Sem uma conta escolhida aqui, o fechamento automático fica
                        desligado mesmo com o parâmetro marcado.
                      </span>
                    </label>
                  )}
                </div>

                {org.pdv_config?.botao_pesados && (
                  <div style={{
                    border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginBottom: 20,
                    background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  }}>
                    <strong style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                      Botão Pesados — quais produtos aparecem
                    </strong>
                    <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block', marginBottom: 10 }}>
                      A tela do botão Pesados mostra os produtos ativos que tiverem marcado, no Cad. Produtos,
                      o atributo escolhido aqui. <strong>Exemplo:</strong> com "Pesado no caixa", aparecem
                      banana, tomate, carne etc. que já são pesados no PDV.
                    </span>
                    {[
                      ['pesado_no_caixa', 'Pesado no caixa'],
                      ['produto_balanca', 'Produto de balança'],
                      ['marcador', 'Marcador'],
                    ].map(([valor, rotulo]) => (
                      <label key={valor} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 6 }}>
                        <input type="radio" name="pesados_filtro"
                          checked={(org.pdv_config?.pesados_filtro || 'pesado_no_caixa') === valor}
                          onChange={() => setO('pdv_config', { ...org.pdv_config, pesados_filtro: valor })} />
                        {rotulo}
                      </label>
                    ))}
                  </div>
                )}

                {org.pdv_config?.controle_mesas_comandas && (
                  <div style={{
                    border: '1px solid #bfdbfe', borderRadius: 8, padding: 20, marginBottom: 20,
                    background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                  }}>
                    <strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
                      Mesas e Comandas
                    </strong>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                        Quantidade de mesas
                        <input type="number" min="1" max="500" style={{ width: 100, padding: '6px 8px' }}
                          value={org.pdv_config?.quantidade_mesas ?? ''}
                          onChange={e => setO('pdv_config', { ...org.pdv_config, quantidade_mesas: e.target.value })} />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                        Prefixo da mesa
                        <input type="text" style={{ width: 140, padding: '6px 8px' }}
                          placeholder="MESA"
                          value={org.pdv_config?.prefixo_mesa ?? ''}
                          onChange={e => setO('pdv_config', { ...org.pdv_config, prefixo_mesa: e.target.value })} />
                      </label>
                      <button type="button" onClick={sincronizarMesas} disabled={sincronizandoMesas}
                        style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #93c5fd',
                          background: '#eff6ff', color: '#1e40af', fontSize: 12.5, cursor: 'pointer' }}
                        title="Cria as mesas numeradas que ainda não existem (1 até a quantidade informada); nunca apaga ou renumera as que já existem.">
                        {sincronizandoMesas ? 'Sincronizando…' : '🔄 Gerar/Sincronizar Mesas'}
                      </button>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                        Tolerância p/ reserva não comparecer (min)
                        <input type="number" min="1" max="240" style={{ width: 100, padding: '6px 8px' }}
                          placeholder="30"
                          value={org.pdv_config?.tolerancia_reserva_min ?? ''}
                          onChange={e => setO('pdv_config', { ...org.pdv_config, tolerancia_reserva_min: e.target.value })} />
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                        Quantidade de comandas
                        <input type="number" min="1" max="500" style={{ width: 100, padding: '6px 8px' }}
                          value={org.pdv_config?.quantidade_comandas ?? ''}
                          onChange={e => setO('pdv_config', { ...org.pdv_config, quantidade_comandas: e.target.value })} />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                        Prefixo comandas
                        <input type="text" style={{ width: 140, padding: '6px 8px' }}
                          placeholder="COMANDA"
                          value={org.pdv_config?.prefixo_comandas ?? ''}
                          onChange={e => setO('pdv_config', { ...org.pdv_config, prefixo_comandas: e.target.value })} />
                      </label>
                      <button type="button" onClick={sincronizarComandas} disabled={sincronizandoComandas}
                        style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #93c5fd',
                          background: '#eff6ff', color: '#1e40af', fontSize: 12.5, cursor: 'pointer' }}
                        title="Cria as comandas avulsas numeradas que ainda não existem (1 até a quantidade informada); nunca apaga ou renumera as que já existem. Numeração independente da numeração das mesas.">
                        {sincronizandoComandas ? 'Sincronizando…' : '🔄 Gerar/Sincronizar Comandas'}
                      </button>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" style={{ marginTop: 3 }}
                        checked={!!org.pdv_config?.multiplas_comandas_por_mesa}
                        onChange={e => setO('pdv_config', { ...org.pdv_config, multiplas_comandas_por_mesa: e.target.checked })} />
                      <span>
                        <strong style={{ fontSize: 13, display: 'block', marginBottom: 3 }}>Permitir múltiplas comandas por mesa</strong>
                        <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block' }}>
                          Desligado (padrão): só uma comanda aberta por vez em cada mesa. A divisão de conta
                          entre várias comandas simultâneas na mesma mesa ainda não tem tela própria.
                        </span>
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 14 }}>
                      <input type="checkbox" style={{ marginTop: 3 }}
                        checked={!!org.pdv_config?.abrir_mesas_ao_finalizar_mesa}
                        onChange={e => setO('pdv_config', { ...org.pdv_config, abrir_mesas_ao_finalizar_mesa: e.target.checked })} />
                      <span>
                        <strong style={{ fontSize: 13, display: 'block', marginBottom: 3 }}>Abrir tela de Mesas automaticamente ao finalizar uma mesa no PDV</strong>
                        <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block' }}>
                          Desligado (padrão): depois de fechar a conta de uma mesa, o PDV volta pro carrinho vazio
                          normal. Ligado: assim que a venda da mesa termina (com ou sem nota emitida), o F1
                          reabre sozinho já na aba Mesas — útil pra quem atende mesa direto atrás da outra.
                        </span>
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 14 }}>
                      <input type="checkbox" style={{ marginTop: 3 }}
                        checked={!!org.pdv_config?.abrir_comandas_ao_finalizar_comanda}
                        onChange={e => setO('pdv_config', { ...org.pdv_config, abrir_comandas_ao_finalizar_comanda: e.target.checked })} />
                      <span>
                        <strong style={{ fontSize: 13, display: 'block', marginBottom: 3 }}>Abrir tela de Comandas automaticamente ao finalizar uma comanda no PDV</strong>
                        <span style={{ fontSize: 12, color: '#444', lineHeight: 1.5, display: 'block' }}>
                          Mesma ideia da opção acima, só que pra comanda avulsa (sem mesa vinculada) — reabre o F1
                          já na aba Comandas. Independente da opção de Mesas: dá pra ligar só uma das duas.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <p style={{ fontSize: 13, color: '#444', marginBottom: 16 }}>
                  Escolha o layout da tela do PDV. A alteração é salva junto com os demais dados da organização.
                </p>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {[
                    {
                      id: 1,
                      titulo: 'Layout 1 — Padrão',
                      desc: 'Logo centralizada com espaço superior e informações compactas abaixo.',
                      preview: (
                        <svg viewBox="0 0 200 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', display: 'block' }}>
                          {/* ── Sidebar esquerda (56px) ── */}
                          <rect x="0" y="0" width="56" height="130" fill="#1a1a2e"/>
                          {/* Espaço vazio superior — logo fica centralizada */}
                          {/* Logo centralizada (~y=28 ao y=72) */}
                          <rect x="7" y="28" width="42" height="44" rx="4" fill="#2563eb"/>
                          <text x="28" y="52" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">LOGO</text>
                          {/* EMPRESA */}
                          <rect x="0" y="72" width="56" height="16" fill="rgba(255,255,255,0.05)" />
                          <text x="5" y="78" fontSize="3.5" fill="rgba(255,255,255,0.35)">EMPRESA</text>
                          <text x="5" y="85" fontSize="5" fill="rgba(255,255,255,0.82)" fontWeight="bold">R E DE O SILVA</text>
                          {/* Caixa Fechado */}
                          <rect x="3" y="90" width="50" height="9" rx="2" fill="#fee2e2"/>
                          <text x="28" y="96.5" textAnchor="middle" fontSize="3.8" fill="#b91c1c" fontWeight="bold">{">"} Caixa Fechado — clique</text>
                          {/* F10 — CLIENTE */}
                          <rect x="0" y="101" width="56" height="15" fill="rgba(255,255,255,0.06)"/>
                          <text x="5" y="107" fontSize="3.5" fill="rgba(255,255,255,0.35)">F10 — CLIENTE</text>
                          <text x="5" y="113" fontSize="5" fill="#3b82f6" fontWeight="bold">Não informado</text>
                          {/* MOV badge */}
                          <rect x="0" y="122" width="56" height="8" fill="rgba(255,255,255,0.06)"/>
                          <text x="5" y="128" fontSize="3.8" fill="rgba(255,255,255,0.35)">MOV.</text>
                          <text x="18" y="128" fontSize="3.8" fill="rgba(255,255,255,0.45)">Venda Direta</text>

                          {/* ── Área direita (x=56, width=144) ── */}
                          <rect x="56" y="0" width="144" height="130" fill="#111827"/>
                          {/* Cabeçalho colunas */}
                          <rect x="56" y="0" width="144" height="11" fill="#1f2937"/>
                          <text x="60" y="8" fontSize="4" fill="#9ca3af">Cód.  Descrição                      Qtd.   UN   Preço Unit. R$   Total R$</text>
                          {/* Tabela branca */}
                          <rect x="56" y="11" width="144" height="57" fill="#fff"/>
                          <text x="128" y="41" textAnchor="middle" fontSize="5.5" fill="#9ca3af" fontStyle="italic">Nenhum item adicionado — busque abaixo</text>
                          {/* Barra de input */}
                          <rect x="56" y="68" width="144" height="19" fill="#1c1c2e"/>
                          <text x="60" y="74.5" fontSize="3.5" fill="#6b7280">QTD.:</text>
                          <rect x="60" y="76" width="18" height="8" rx="1" fill="#111"/>
                          <text x="69" y="82" textAnchor="middle" fontSize="4" fill="#fff">1</text>
                          <text x="82" y="74.5" fontSize="3.5" fill="#6b7280">PREÇO UNIT. R$:</text>
                          <rect x="82" y="76" width="26" height="8" rx="1" fill="#111"/>
                          <text x="95" y="82" textAnchor="middle" fontSize="4" fill="#fbbf24">0,00</text>
                          <text x="113" y="74.5" fontSize="3.5" fill="#6b7280">TOTAL R$:</text>
                          <rect x="113" y="76" width="25" height="8" rx="1" fill="#111"/>
                          <text x="125.5" y="82" textAnchor="middle" fontSize="4" fill="#fbbf24">0,00</text>
                          {/* Campo busca */}
                          <rect x="56" y="87" width="124" height="9" rx="1" fill="#fff"/>
                          <text x="60" y="93.5" fontSize="3.8" fill="#9ca3af">Código de barras ou descrição do produto...</text>
                          {/* ERP VAREJO canto */}
                          <rect x="180" y="87" width="20" height="9" fill="#111827"/>
                          <text x="190" y="91" textAnchor="middle" fontSize="3" fill="#2563eb" fontWeight="bold">ERP</text>
                          <text x="190" y="95" textAnchor="middle" fontSize="2.8" fill="#9ca3af">VAREJO</text>
                          {/* ITENS / TOTAL R$ */}
                          <rect x="56" y="96" width="144" height="12" fill="#0f172a"/>
                          <text x="60" y="101" fontSize="3.5" fill="#9ca3af">ITENS</text>
                          <text x="60" y="106" fontSize="5" fill="#3b82f6" fontWeight="bold">0</text>
                          <text x="196" y="106" textAnchor="end" fontSize="6" fill="#ef4444" fontWeight="bold">0,00</text>
                          {/* Usuário */}
                          <rect x="56" y="108" width="144" height="8" fill="#1f2937"/>
                          <text x="60" y="113.5" fontSize="4" fill="#e5e7eb" fontWeight="bold">Administrador</text>
                          <text x="60" y="117" fontSize="3" fill="#9ca3af">ADMIN</text>
                          {/* Teclas de função */}
                          <rect x="56" y="116" width="144" height="14" fill="#111827"/>
                          {[['F2','Pré-venda'],['F3','Busca'],['F7','Moviment.'],['F9','Salvar'],['F10','Cliente'],['Ctrl','Opções'],['Del','Remover']].map(([k,l],i) => (
                            <g key={k}>
                              <rect x={57+i*20} y="117" width="18" height="12" rx="2" fill="#1f2937"/>
                              <text x={66+i*20} y="122" textAnchor="middle" fontSize="3.5" fill="#3b82f6" fontWeight="bold">{k}</text>
                              <text x={66+i*20} y="127" textAnchor="middle" fontSize="2.8" fill="#9ca3af">{l}</text>
                            </g>
                          ))}
                        </svg>
                      ),
                    },
                    {
                      id: 2,
                      titulo: 'Layout 2 — Sidebar Informativa',
                      desc: 'Logo no topo, com cliente, vendedor, pagamento e observações visíveis.',
                      preview: (
                        <svg viewBox="0 0 200 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', display: 'block' }}>
                          {/* ── Sidebar esquerda (56px) — logo no TOPO ── */}
                          <rect x="0" y="0" width="56" height="130" fill="#1a1a2e"/>
                          {/* Logo no topo */}
                          <rect x="7" y="4" width="42" height="26" rx="3" fill="#2563eb"/>
                          <text x="28" y="19" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">LOGO</text>
                          {/* EMPRESA */}
                          <text x="5" y="37" fontSize="3.5" fill="rgba(255,255,255,0.35)">EMPRESA</text>
                          <text x="5" y="44" fontSize="5" fill="rgba(255,255,255,0.82)" fontWeight="bold">R E DE O SILVA</text>
                          {/* Caixa Fechado */}
                          <rect x="3" y="47" width="50" height="8" rx="2" fill="#fee2e2"/>
                          <text x="28" y="53" textAnchor="middle" fontSize="3.8" fill="#b91c1c" fontWeight="bold">{">"} Caixa Fechado</text>
                          {/* F10 — CLIENTE */}
                          <text x="5" y="63" fontSize="3.5" fill="rgba(255,255,255,0.35)">F10 — CLIENTE</text>
                          <text x="5" y="70" fontSize="4.5" fill="#3b82f6">Não informado</text>
                          {/* VENDEDOR */}
                          <text x="5" y="78" fontSize="3.5" fill="rgba(255,255,255,0.35)">VENDEDOR</text>
                          <text x="5" y="85" fontSize="4.5" fill="#3b82f6">Não informado</text>
                          {/* FORMA DE PAGAMENTO */}
                          <text x="5" y="92" fontSize="3.2" fill="rgba(255,255,255,0.35)">FORMA DE PAGAMENTO</text>
                          <text x="5" y="99" fontSize="4.5" fill="#3b82f6">À vista</text>
                          {/* OBSERVAÇÕES */}
                          <text x="5" y="107" fontSize="3.5" fill="rgba(255,255,255,0.35)">OBSERVAÇÕES</text>
                          <text x="5" y="114" fontSize="4" fill="#3b82f6" fontStyle="italic">Nenhuma observação</text>
                          {/* MOV bottom */}
                          <rect x="0" y="122" width="56" height="8" fill="rgba(255,255,255,0.06)"/>
                          <text x="5" y="128" fontSize="3.8" fill="rgba(255,255,255,0.35)">MOV.</text>
                          <text x="18" y="128" fontSize="3.8" fill="rgba(255,255,255,0.45)">Venda Direta</text>

                          {/* ── Área direita (x=56, width=144) ── */}
                          <rect x="56" y="0" width="144" height="130" fill="#111827"/>
                          {/* Cabeçalho colunas */}
                          <rect x="56" y="0" width="144" height="11" fill="#1f2937"/>
                          <text x="60" y="8" fontSize="4" fill="#9ca3af">Cód.  Descrição                      Qtd.   UN   Preço Unit. R$   Total R$</text>
                          {/* Tabela branca */}
                          <rect x="56" y="11" width="144" height="57" fill="#fff"/>
                          <text x="128" y="41" textAnchor="middle" fontSize="5.5" fill="#9ca3af" fontStyle="italic">Nenhum item adicionado — busque abaixo</text>
                          {/* Barra de input */}
                          <rect x="56" y="68" width="144" height="19" fill="#1c1c2e"/>
                          <text x="60" y="74.5" fontSize="3.5" fill="#6b7280">QTD.:</text>
                          <rect x="60" y="76" width="18" height="8" rx="1" fill="#111"/>
                          <text x="69" y="82" textAnchor="middle" fontSize="4" fill="#fff">0</text>
                          <text x="82" y="74.5" fontSize="3.5" fill="#6b7280">PREÇO UNIT. R$:</text>
                          <rect x="82" y="76" width="26" height="8" rx="1" fill="#111"/>
                          <text x="95" y="82" textAnchor="middle" fontSize="4" fill="#fbbf24">0,00</text>
                          <text x="113" y="74.5" fontSize="3.5" fill="#6b7280">TOTAL R$:</text>
                          <rect x="113" y="76" width="25" height="8" rx="1" fill="#111"/>
                          <text x="125.5" y="82" textAnchor="middle" fontSize="4" fill="#fbbf24">0,00</text>
                          {/* Campo busca */}
                          <rect x="56" y="87" width="124" height="9" rx="1" fill="#fff"/>
                          <text x="60" y="93.5" fontSize="3.8" fill="#9ca3af">Código de barras ou descrição do produto...</text>
                          <rect x="180" y="87" width="20" height="9" fill="#111827"/>
                          <text x="190" y="91" textAnchor="middle" fontSize="3" fill="#2563eb" fontWeight="bold">ERP</text>
                          <text x="190" y="95" textAnchor="middle" fontSize="2.8" fill="#9ca3af">VAREJO</text>
                          {/* ITENS / TOTAL R$ */}
                          <rect x="56" y="96" width="144" height="12" fill="#0f172a"/>
                          <text x="60" y="101" fontSize="3.5" fill="#9ca3af">ITENS</text>
                          <text x="60" y="106" fontSize="5" fill="#3b82f6" fontWeight="bold">0</text>
                          <text x="196" y="106" textAnchor="end" fontSize="6" fill="#ef4444" fontWeight="bold">0,00</text>
                          <text x="196" y="110" textAnchor="end" fontSize="7" fill="#f87171" fontWeight="bold">0,00</text>
                          {/* Rodapé */}
                          <rect x="48" y="114" width="152" height="16" fill="#1f2937"/>
                          {['F2','F3','F7','F9','F10','Ctrl','Del'].map((t,i) => (
                            <g key={t}>
                              <rect x={49+i*21} y="115" width="19" height="14" rx="2" fill="#374151"/>
                              <text x={58.5+i*21} y="125" textAnchor="middle" fontSize="4.5" fill="#60a5fa" fontWeight="bold">{t}</text>
                            </g>
                          ))}
                        </svg>
                      ),
                    },
                    {
                      id: 3,
                      titulo: 'Layout 3 — Moderno E+ Gestor',
                      desc: 'Tela clara com cards de status, resumo da venda à direita e barra de ações em botões.',
                      preview: (
                        <svg viewBox="0 0 200 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', display: 'block' }}>
                          <rect x="0" y="0" width="200" height="130" fill="#f1f5f9"/>
                          {/* Sidebar branca fina */}
                          <rect x="0" y="0" width="22" height="130" fill="#ffffff"/>
                          <rect x="4" y="4" width="14" height="10" rx="2" fill="#2563eb"/>
                          <text x="11" y="12" textAnchor="middle" fontSize="6" fill="#fff" fontWeight="bold">E+</text>
                          {[22, 36, 50, 64, 78].map(y => <rect key={y} x="5" y={y} width="12" height="9" rx="2" fill={y === 22 ? '#dbeafe' : '#eef2f7'}/>)}
                          {/* Header */}
                          <rect x="22" y="0" width="178" height="14" fill="#ffffff"/>
                          <text x="27" y="9" fontSize="6.5" fill="#0f172a" fontWeight="bold">PDV</text>
                          <rect x="150" y="3" width="20" height="8" rx="2" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.6"/>
                          <rect x="172" y="3" width="24" height="8" rx="2" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.6"/>
                          {/* Cards de status */}
                          {[26, 61, 96, 131, 166].map((x, i) => (
                            <rect key={x} x={x} y="18" width="32" height="14" rx="3" fill="#ffffff" stroke="#e8edf3" strokeWidth="0.6"/>
                          ))}
                          <circle cx="31" cy="25" r="3" fill="#dcfce7"/>
                          <circle cx="66" cy="25" r="3" fill="#dbeafe"/>
                          <circle cx="101" cy="25" r="3" fill="#ffedd5"/>
                          <circle cx="136" cy="25" r="3" fill="#ede9fe"/>
                          <circle cx="171" cy="25" r="3" fill="#ccfbf1"/>
                          {/* Área central: busca + tabela */}
                          <rect x="26" y="36" width="115" height="70" rx="4" fill="#ffffff" stroke="#e8edf3" strokeWidth="0.6"/>
                          <rect x="30" y="40" width="95" height="8" rx="2" fill="#f8fafc" stroke="#dbe3ee" strokeWidth="0.6"/>
                          <rect x="127" y="40" width="10" height="8" rx="2" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="0.6"/>
                          <rect x="30" y="52" width="107" height="7" fill="#f8fafc"/>
                          {[61, 69].map(y => <rect key={y} x="30" y={y} width="107" height="6" fill={y === 61 ? '#fff' : '#fbfdff'} stroke="#f1f5f9" strokeWidth="0.4"/>)}
                          <rect x="97" y="97" width="40" height="7" rx="2" fill="#fff" stroke="#fecaca" strokeWidth="0.6"/>
                          <text x="117" y="102" textAnchor="middle" fontSize="3.6" fill="#dc2626" fontWeight="bold">LIMPAR ITENS</text>
                          {/* Resumo à direita */}
                          <rect x="145" y="36" width="51" height="70" rx="4" fill="#ffffff" stroke="#e8edf3" strokeWidth="0.6"/>
                          <text x="149" y="44" fontSize="4" fill="#0f172a" fontWeight="bold">RESUMO DA VENDA</text>
                          <text x="149" y="66" fontSize="4" fill="#0f172a" fontWeight="bold">TOTAL GERAL</text>
                          <text x="149" y="78" fontSize="9" fill="#2563eb" fontWeight="bold">R$ 22,50</text>
                          <rect x="149" y="84" width="43" height="9" rx="2" fill="#fef9c3" stroke="#fde68a" strokeWidth="0.6"/>
                          <text x="152" y="90" fontSize="3.6" fill="#92400e">F9 - Finalizar venda</text>
                          {/* Barra de ações */}
                          {[26, 51, 76, 101, 126, 151].map(x => (
                            <rect key={x} x={x} y="110" width="22" height="13" rx="3" fill="#ffffff" stroke="#e8edf3" strokeWidth="0.6"/>
                          ))}
                          <rect x="176" y="110" width="20" height="13" rx="3" fill="#16a34a"/>
                          <text x="186" y="118" textAnchor="middle" fontSize="4" fill="#fff" fontWeight="bold">F12</text>
                        </svg>
                      ),
                    },
                    {
                      id: 4,
                      titulo: 'Layout 4 — Sidebar Informativa (claro)',
                      desc: 'Cópia fiel do Layout 2 (mesmas seções: cliente, vendedor, pagamento, observações), em cor clara.',
                      preview: (
                        <svg viewBox="0 0 200 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', display: 'block' }}>
                          {/* ── Sidebar esquerda (56px) — logo no TOPO, cor clara ── */}
                          <rect x="0" y="0" width="56" height="130" fill="#eef5fc"/>
                          {/* Logo no topo */}
                          <rect x="7" y="4" width="42" height="26" rx="3" fill="#2563eb"/>
                          <text x="28" y="19" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">LOGO</text>
                          {/* EMPRESA */}
                          <text x="5" y="37" fontSize="3.5" fill="#94a3b8">EMPRESA</text>
                          <text x="5" y="44" fontSize="5" fill="#0f172a" fontWeight="bold">R E DE O SILVA</text>
                          {/* Caixa Fechado */}
                          <rect x="3" y="47" width="50" height="8" rx="2" fill="#fee2e2"/>
                          <text x="28" y="53" textAnchor="middle" fontSize="3.8" fill="#b91c1c" fontWeight="bold">{">"} Caixa Fechado</text>
                          {/* F10 — CLIENTE */}
                          <text x="5" y="63" fontSize="3.5" fill="#94a3b8">F10 — CLIENTE</text>
                          <text x="5" y="70" fontSize="4.5" fill="#2563eb">Não informado</text>
                          {/* VENDEDOR */}
                          <text x="5" y="78" fontSize="3.5" fill="#94a3b8">VENDEDOR</text>
                          <text x="5" y="85" fontSize="4.5" fill="#2563eb">Não informado</text>
                          {/* FORMA DE PAGAMENTO */}
                          <text x="5" y="92" fontSize="3.2" fill="#94a3b8">FORMA DE PAGAMENTO</text>
                          <text x="5" y="99" fontSize="4.5" fill="#2563eb">À vista</text>
                          {/* OBSERVAÇÕES */}
                          <text x="5" y="107" fontSize="3.5" fill="#94a3b8">OBSERVAÇÕES</text>
                          <text x="5" y="114" fontSize="4" fill="#2563eb" fontStyle="italic">Nenhuma observação</text>
                          {/* MOV bottom */}
                          <rect x="0" y="122" width="56" height="8" fill="#f8fafc"/>
                          <text x="5" y="128" fontSize="3.8" fill="#94a3b8">MOV.</text>
                          <text x="18" y="128" fontSize="3.8" fill="#475569">Venda Direta</text>

                          {/* ── Área direita (x=56, width=144) — clara ── */}
                          <rect x="56" y="0" width="144" height="130" fill="#ffffff"/>
                          {/* Cabeçalho colunas */}
                          <rect x="56" y="0" width="144" height="11" fill="#f8fafc"/>
                          <text x="60" y="8" fontSize="4" fill="#64748b">Cód.  Descrição                      Qtd.   UN   Preço Unit. R$   Total R$</text>
                          {/* Tabela branca */}
                          <rect x="56" y="11" width="144" height="57" fill="#fff"/>
                          <text x="128" y="41" textAnchor="middle" fontSize="5.5" fill="#94a3b8" fontStyle="italic">Nenhum item adicionado — busque abaixo</text>
                          {/* Barra de input */}
                          <rect x="56" y="68" width="144" height="19" fill="#ffffff"/>
                          <rect x="56" y="68" width="144" height="1" fill="#e2e8f0"/>
                          <text x="60" y="74.5" fontSize="3.5" fill="#94a3b8">QTD.:</text>
                          <rect x="60" y="76" width="18" height="8" rx="1" fill="#fff" stroke="#cbd5e1" strokeWidth="0.6"/>
                          <text x="69" y="82" textAnchor="middle" fontSize="4" fill="#0f172a">0</text>
                          <text x="82" y="74.5" fontSize="3.5" fill="#94a3b8">PREÇO UNIT. R$:</text>
                          <rect x="82" y="76" width="26" height="8" rx="1" fill="#fff" stroke="#cbd5e1" strokeWidth="0.6"/>
                          <text x="95" y="82" textAnchor="middle" fontSize="4" fill="#0f172a">0,00</text>
                          <text x="113" y="74.5" fontSize="3.5" fill="#94a3b8">TOTAL R$:</text>
                          <rect x="113" y="76" width="25" height="8" rx="1" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="0.6"/>
                          <text x="125.5" y="82" textAnchor="middle" fontSize="4" fill="#1d4ed8">0,00</text>
                          {/* Campo busca */}
                          <rect x="56" y="87" width="124" height="9" rx="1" fill="#fff" stroke="#cbd5e1" strokeWidth="0.6"/>
                          <text x="60" y="93.5" fontSize="3.8" fill="#94a3b8">Código de barras ou descrição do produto...</text>
                          {/* ERP VAREJO canto */}
                          <text x="190" y="91" textAnchor="middle" fontSize="3" fill="#2563eb" fontWeight="bold">ERP</text>
                          <text x="190" y="95" textAnchor="middle" fontSize="2.8" fill="#94a3b8">VAREJO</text>
                          {/* ITENS / TOTAL R$ */}
                          <rect x="56" y="96" width="144" height="12" fill="#fff"/>
                          <rect x="56" y="96" width="144" height="1" fill="#e2e8f0"/>
                          <text x="60" y="101" fontSize="3.5" fill="#94a3b8">ITENS</text>
                          <text x="60" y="106" fontSize="5" fill="#2563eb" fontWeight="bold">0</text>
                          <text x="196" y="106" textAnchor="end" fontSize="6" fill="#94a3b8">TOTAL R$</text>
                          <text x="196" y="112" textAnchor="end" fontSize="7" fill="#2563eb" fontWeight="bold">0,00</text>
                          {/* Usuário */}
                          <rect x="56" y="108" width="144" height="8" fill="#fff"/>
                          <text x="60" y="113.5" fontSize="4" fill="#0f172a" fontWeight="bold">Administrador</text>
                          <text x="60" y="117" fontSize="3" fill="#94a3b8">ADMIN</text>
                          {/* Teclas de função */}
                          <rect x="56" y="116" width="144" height="14" fill="#fff"/>
                          <rect x="56" y="116" width="144" height="1" fill="#e2e8f0"/>
                          {[['F2','Pré-venda'],['F3','Busca'],['F7','Moviment.'],['F9','Salvar'],['F10','Cliente'],['Ctrl','Opções'],['Del','Remover']].map(([k,l],i) => (
                            <g key={k}>
                              <rect x={57+i*20} y="117" width="18" height="12" rx="2" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="0.6"/>
                              <text x={66+i*20} y="122" textAnchor="middle" fontSize="3.5" fill="#1d4ed8" fontWeight="bold">{k}</text>
                              <text x={66+i*20} y="127" textAnchor="middle" fontSize="2.8" fill="#475569">{l}</text>
                            </g>
                          ))}
                        </svg>
                      ),
                    },
                  ].map(layout => {
                    const sel = (org.pdv_layout ?? 1) === layout.id;
                    return (
                      <div key={layout.id}
                        onClick={() => isEdicao && isAdmin && setO('pdv_layout', layout.id)}
                        style={{
                          flex: '1 1 280px', maxWidth: 320,
                          border: `2px solid ${sel ? '#2563eb' : '#d1d5db'}`,
                          borderRadius: 10, overflow: 'hidden',
                          cursor: isEdicao && isAdmin ? 'pointer' : 'default',
                          background: sel ? '#eff6ff' : '#fff',
                          boxShadow: sel ? '0 0 0 3px rgba(37,99,235,0.15)' : '0 1px 4px rgba(0,0,0,0.07)',
                          transition: 'all 0.15s',
                        }}>
                        <div style={{ padding: '10px 14px 6px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 4 }}>
                            <strong style={{ fontSize: 13, color: sel ? '#1d4ed8' : '#111' }}>{layout.titulo}</strong>
                            {sel && <span style={{ fontSize: 11, background: '#2563eb', color: '#fff', borderRadius: 10, padding: '1px 8px' }}>Ativo</span>}
                          </div>
                          <p style={{ fontSize: 11, color: '#666', margin: 0, marginBottom: 8 }}>{layout.desc}</p>
                        </div>
                        <div style={{ background: '#0f0f1a', margin: '0 10px 10px', borderRadius: 6, overflow: 'hidden' }}>
                          {layout.preview}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {isEdicao && isAdmin && (
                  <p style={{ fontSize: 11, color: '#888', marginTop: 12, fontStyle: 'italic' }}>
                    Clique no layout desejado e salve a organização para aplicar.
                  </p>
                )}
              </div>
            )}

            {subAbaParam === 'rotinas' && (
              <div style={{ maxWidth: 700 }}>

                {/* Resultado da limpeza */}
                {resultadoLimpar && (
                  <div style={{
                    background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8,
                    padding: 16, marginBottom: 20,
                  }}>
                    <strong style={{ color: '#166534', fontSize: 14 }}>Limpeza concluída com sucesso!</strong>
                    <table style={{ marginTop: 10, fontSize: 12, borderCollapse: 'collapse', width: '100%' }}>
                      <tbody>
                        {Object.entries(LABELS_RESUMO_LIMPEZA)
                          .filter(([chave]) => resultadoLimpar[chave] !== undefined)
                          .map(([chave, label]) => (
                            <tr key={chave}>
                              <td style={{ padding: '2px 12px 2px 0', color: '#444' }}>{label}</td>
                              <td style={{ color: '#166534', fontWeight: 'bold' }}>{resultadoLimpar[chave]} registro(s)</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    <button style={{ marginTop: 10, fontSize: 12, padding: '3px 10px', cursor: 'pointer' }}
                      onClick={() => setResultadoLimpar(null)}>Fechar</button>
                  </div>
                )}

                {/* Rotina: Pagamento de Comissão */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 28 }}>💰</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 14, color: '#1e40af', display: 'block', marginBottom: 6 }}>
                        Pagamento de Comissão
                      </strong>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                        Define <strong>quando</strong> a comissão passa a ser devida e
                        <strong> de onde vem o percentual</strong> — base do relatório de comissões
                        (Relatórios → Comissões). O % é gravado item a item na venda.
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12, fontWeight: 600 }}>Comissão devida na:</label>
                        <select className={s.input} style={{ width: 240 }}
                          value={org.comissao_pagamento}
                          onChange={e => setO('comissao_pagamento', e.target.value)}>
                          <option value="emissao_venda">Emissão da venda</option>
                          <option value="baixa_cr">Baixa do contas a receber</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12, fontWeight: 600 }}>Percentual da comissão:</label>
                        <select className={s.input} style={{ width: 240 }}
                          value={org.comissao_base || 'produto'}
                          onChange={e => setO('comissao_base', e.target.value)}>
                          <option value="produto">Por produto (Comissão % de cada produto)</option>
                          <option value="vendedor">Por vendedor (Comissão % do usuário)</option>
                        </select>
                      </div>
                      <p style={{ margin: '8px 0 0', fontSize: 11, color: '#666' }}>
                        Emissão da venda: conta a partir da finalização. Baixa do contas a receber:
                        só quando o título da venda é efetivamente recebido (proporcional à parcela;
                        vendas à vista contam na data da venda). Por vendedor: o % vem do campo
                        "Comissão %" do cadastro de usuários.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rotina: Limite de crédito do cliente */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 28 }}>💳</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 14, color: '#1e40af', display: 'block', marginBottom: 6 }}>
                        Validar Limite de Crédito
                      </strong>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                        Com esta opção marcada, o PDV e os Pedidos/Orçamentos pedem senha de admin/gerente
                        para finalizar uma venda a prazo (Boleto ou Crediário) quando o cliente não tem
                        limite de crédito definido no cadastro, ou quando o valor da venda ultrapassa o
                        crédito disponível (limite − contas a receber em aberto de boletos/crediários).
                        Usuários com "Libera Limite de Crédito" no cadastro (Administrativo → Usuários)
                        não precisam de senha.
                      </p>
                      <div className={s.checkRow}>
                        <input type="checkbox" id="chkValidarLimiteCredito"
                          checked={org.validar_limite_credito}
                          onChange={e => setO('validar_limite_credito', e.target.checked)} />
                        <label htmlFor="chkValidarLimiteCredito">Validar limite de crédito do cliente em vendas a prazo</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rotina: Estoque insuficiente */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 28 }}>📦</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 14, color: '#1e40af', display: 'block', marginBottom: 6 }}>
                        Validar Estoque Insuficiente
                      </strong>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                        Com esta opção marcada, o PDV e os Pedidos/Orçamentos pedem senha de admin/gerente
                        para finalizar uma venda que deixaria o estoque negativo. Só vale para os Tipos de
                        Movimento com "Verificar estoque insuficiente" marcado (Cad. Produtos → Tipos de
                        Movimento) — sem essa marcação no tipo usado, a venda não é validada. Usuários com
                        "Libera Estoque Insuficiente" no cadastro (Administrativo → Usuários) não precisam
                        de senha.
                      </p>
                      <div className={s.checkRow}>
                        <input type="checkbox" id="chkValidarEstoqueInsuficiente"
                          checked={org.validar_estoque_insuficiente}
                          onChange={e => setO('validar_estoque_insuficiente', e.target.checked)} />
                        <label htmlFor="chkValidarEstoqueInsuficiente">Validar estoque insuficiente nas vendas</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rotina: Atraso do cliente */}
                <div style={{
                  border: '1px solid #bfdbfe', borderRadius: 8, padding: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 28 }}>⏰</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 14, color: '#1e40af', display: 'block', marginBottom: 6 }}>
                        Validar Atraso do Cliente
                      </strong>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                        Com esta opção marcada, o PDV e os Pedidos/Orçamentos avisam quando o cliente tem
                        boletos ou crediários vencidos em aberto. Se a nova venda for à vista, é só um
                        lembrete (o operador confirma e segue); se a nova venda também for a prazo (Boleto
                        ou Crediário), pede senha de admin/gerente. Usuários com "Libera Cliente
                        Inadimplente" no cadastro (Administrativo → Usuários) não precisam de senha.
                      </p>
                      <div className={s.checkRow}>
                        <input type="checkbox" id="chkValidarAtrasoCliente"
                          checked={org.validar_atraso_cliente}
                          onChange={e => setO('validar_atraso_cliente', e.target.checked)} />
                        <label htmlFor="chkValidarAtrasoCliente">Validar atraso do cliente nas vendas</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card da rotina */}
                <div style={{
                  border: '1px solid #fca5a5', borderRadius: 8, padding: 20,
                  background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ fontSize: 28 }}>🗑</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 14, color: '#b91c1c', display: 'block', marginBottom: 6 }}>
                        Limpar Movimentações
                      </strong>
                      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#444', lineHeight: 1.6 }}>
                        Remove <strong>permanentemente</strong> os dados operacionais dos módulos
                        selecionados abaixo. Os dados cadastrais são mantidos e o estoque é
                        recalculado pelas movimentações que restarem.
                      </p>
                      <div style={{ marginBottom: 10 }}>
                        <strong style={{ color: '#b91c1c', fontSize: 12, display: 'block', marginBottom: 6 }}>
                          O que limpar:
                        </strong>
                        {MODULOS_LIMPEZA.map(m => {
                          const travadoPor = MODULOS_LIMPEZA.find(o =>
                            modulosLimpar[o.id] && (o.deps || []).includes(m.id));
                          return (
                            <label key={m.id} title={travadoPor
                              ? `Incluído automaticamente por "${travadoPor.label}" (vínculos estruturais)`
                              : m.desc}
                              style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12,
                                padding: '3px 0', cursor: travadoPor ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                              <input type="checkbox"
                                checked={!!modulosLimpar[m.id]}
                                disabled={!!travadoPor}
                                onChange={() => toggleModuloLimpar(m.id)}
                                style={{ marginTop: 2 }} />
                              <span>
                                <strong>{m.label}</strong>
                                {travadoPor && <em style={{ color: '#b45309' }}> (incluído por {travadoPor.label})</em>}
                                <span style={{ color: '#777', display: 'block' }}>{m.desc}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 12, color: '#166534', marginBottom: 10 }}>
                        <strong>Sempre mantido:</strong> produtos, clientes e fornecedores, bandeiras,
                        contas financeiras, tabelas de preço, parâmetros fiscais, tipos de movimento,
                        usuários e trilha de auditoria.
                      </div>
                      {isAdmin && isEdicao && (
                        <button
                          onClick={() => {
                            setSenhaLimpar(''); setConfirmaLimpar(false);
                            setErroLimpar(''); setModalLimpar(true);
                          }}
                          style={{
                            marginTop: 8, padding: '6px 16px', background: '#b91c1c',
                            color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer',
                            fontSize: 13, fontWeight: 'bold',
                          }}
                        >
                          Executar Limpeza
                        </button>
                      )}
                      {!isAdmin && (
                        <p style={{ fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                          Apenas administradores podem executar rotinas.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {aba === 'certificado' && (
          <div>
            {/* Status do certificado atual */}
            {certInfo && (
              <fieldset className={s.fieldset} style={{ marginBottom: 12 }}>
                <legend className={s.legend}>Certificado Instalado</legend>
                <div className={s.formGrid}>
                  <div className={s.campo}>
                    <span className={s.campoLabel}>Titular:</span>
                    <span style={{ fontWeight: 'bold' }}>{certInfo.titular}</span>
                  </div>
                  <div className={s.campo}>
                    <span className={s.campoLabel}>Tipo:</span>
                    <span>{certInfo.tipo || 'A1'}</span>
                  </div>
                  <div className={s.campo}>
                    <span className={s.campoLabel}>Validade:</span>
                    <span style={{ color: certInfo.expirado ? '#cc0000' : '#007700', fontWeight: 'bold' }}>
                      {certInfo.validade
                        ? new Date(certInfo.validade).toLocaleDateString('pt-BR')
                        : '—'}
                      {certInfo.expirado ? ' ⚠ EXPIRADO' : ' ✔ Válido'}
                    </span>
                  </div>
                  <div className={s.campo}>
                    <span className={s.campoLabel}>Número de série:</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{certInfo.serial}</span>
                  </div>
                </div>
              </fieldset>
            )}

            {/* Upload */}
            <fieldset className={s.fieldset}>
              <legend className={s.legend}>{certInfo ? 'Substituir Certificado' : 'Enviar Certificado Digital'}</legend>
              <div className={s.campo}>
                <span className={s.campoLabel}>Arquivo (.pfx / .p12):</span>
                <input
                  type="file"
                  accept=".pfx,.p12"
                  style={{ flex: 1 }}
                  onChange={e => { setCertFile(e.target.files[0] || null); setCertErro(''); }}
                />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel}>Senha:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    className={s.inputErp}
                    type={certSenhaVisivel ? 'text' : 'password'}
                    style={{ width: 220 }}
                    placeholder="Senha do certificado"
                    value={certSenha}
                    onChange={e => setCertSenha(e.target.value)}
                  />
                  <button
                    type="button"
                    title={certSenhaVisivel ? 'Ocultar senha' : 'Mostrar senha'}
                    onClick={() => setCertSenhaVisivel(v => !v)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 16, padding: '2px 4px', lineHeight: 1,
                      color: '#555',
                    }}
                  >
                    {certSenhaVisivel ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              {certErro   && <p className={s.erroMsg}   style={{ margin: '6px 0' }}>{certErro}</p>}
              {certSucesso && <p className={s.sucessoMsg} style={{ margin: '6px 0' }}>{certSucesso}</p>}
              <div style={{ marginTop: 10 }}>
                <button
                  className={s.btnErpPrimario}
                  onClick={uploadCertificado}
                  disabled={uploadandoCert || !certFile || !certSenha}
                >
                  {uploadandoCert ? 'Enviando...' : '⬆ Enviar Certificado'}
                </button>
              </div>
              <p style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
                Certificado tipo A1 (.pfx/.p12). A senha é armazenada com criptografia AES-256.
                O arquivo não pode ter mais de 5 MB.
              </p>
            </fieldset>
          </div>
        )}

      </div>

      {/* Mensagens */}
      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}

      {/* Rodapé */}
      <div className={s.rodape}>
        <button className={s.btnErp} onClick={() => navigate('/admin/organizacoes')}>
          Cancelar
        </button>
        {isEdicao && isAdmin && (
          <button
            className={s.btnErp}
            style={{ marginLeft: 'auto', marginRight: 8, background: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' }}
            onClick={() => { setSenhaExcluir(''); setErroExcluir(''); setModalExcluir(true); }}
            disabled={salvando}
          >
            Excluir organização
          </button>
        )}
        <button className={s.btnErpPrimario} onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : '✔ Salvar'}
        </button>
      </div>

      {/* Modal de confirmação de exclusão */}
      {modalExcluir && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: 28, width: 380, maxWidth: '90vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <h3 style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: 16 }}>Excluir organização</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#444' }}>
              Esta ação é <strong>irreversível</strong>. Todos os dados desta organização serão permanentemente removidos.
              <br /><br />
              Digite sua senha de administrador para confirmar:
            </p>
            <input
              type="password"
              placeholder="Sua senha"
              value={senhaExcluir}
              onChange={e => setSenhaExcluir(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && excluirOrg()}
              autoFocus
              style={{
                width: '100%', padding: '7px 10px', fontSize: 14,
                border: '1px solid #ccc', borderRadius: 5, boxSizing: 'border-box', marginBottom: 8,
              }}
            />
            {erroExcluir && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{erroExcluir}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button
                className={s.btnErp}
                onClick={() => setModalExcluir(false)}
                disabled={excluindo}
              >
                Cancelar
              </button>
              <button
                className={s.btnErp}
                style={{ background: '#b91c1c', color: '#fff', borderColor: '#b91c1c' }}
                onClick={excluirOrg}
                disabled={excluindo}
              >
                {excluindo ? 'Excluindo...' : 'Confirmar exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de copiar configurações de outra organização */}
      {modalCopiarConfig && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: 28, width: 440, maxWidth: '90vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <h3 style={{ margin: '0 0 8px', color: '#1e40af', fontSize: 16 }}>Copiar Configurações</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#444', lineHeight: 1.6 }}>
              Copia Rotinas, PDV, Vendas, Horários e Bandeiras de cartão da organização escolhida abaixo —
              <strong> substituindo</strong> o que já estiver configurado aqui. O Plano de Contas é a exceção:
              só as contas que faltarem aqui são acrescentadas, nada é apagado ou sobrescrito.
              <br /><br />
              Pensado pra configurar rápido uma loja <strong>nova</strong>, ainda sem venda/lançamento — se
              alguma bandeira de cartão daqui já tiver uso real, a cópia é cancelada por inteiro (nada é alterado).
            </p>
            <label style={{ display: 'block', fontSize: 12.5, color: '#333', marginBottom: 4 }}>
              Copiar configurações de:
            </label>
            <select
              value={origemCopiaId}
              onChange={e => setOrigemCopiaId(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '7px 10px', fontSize: 14,
                border: '1px solid #ccc', borderRadius: 5, boxSizing: 'border-box', marginBottom: 8,
              }}
            >
              <option value="">-- Selecione a organização de origem --</option>
              {orgsParaCopiar.map(o => (
                <option key={o.id} value={o.id}>{o.razao_social}{o.cnpj ? ` — ${o.cnpj}` : ''}</option>
              ))}
            </select>
            {erroCopiarConfig && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{erroCopiarConfig}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button
                className={s.btnErp}
                onClick={() => setModalCopiarConfig(false)}
                disabled={copiandoConfig}
              >
                Cancelar
              </button>
              <button
                className={s.btnErp}
                style={{ background: '#1e40af', color: '#fff', borderColor: '#1e40af' }}
                onClick={copiarConfig}
                disabled={copiandoConfig || !origemCopiaId}
              >
                {copiandoConfig ? 'Copiando...' : 'Copiar Configurações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de limpeza */}
      {modalLimpar && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: 28, width: 440, maxWidth: '90vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ margin: '0 0 6px', color: '#b91c1c', fontSize: 16 }}>
              ⚠ Limpar Movimentações
            </h3>
            <p style={{ margin: '0 0 8px', fontSize: 13, color: '#444', lineHeight: 1.6 }}>
              Esta ação é <strong>irreversível</strong>. Serão removidos permanentemente os dados de:
            </p>
            <ul style={{ margin: '0 0 12px', paddingLeft: 20, fontSize: 13, color: '#b91c1c' }}>
              {modulosSelecionados.map(m => <li key={m.id}><strong>{m.label}</strong></li>)}
            </ul>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#166534' }}>
              Dados cadastrais, produtos, clientes, bandeiras e contas financeiras serão mantidos.
            </p>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
              marginBottom: 14, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={confirmaLimpar}
                onChange={e => setConfirmaLimpar(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }} />
              Entendo que esta ação é irreversível e desejo continuar
            </label>

            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>
              Senha de administrador para confirmar:
            </label>
            <input
              type="password"
              placeholder="Sua senha"
              value={senhaLimpar}
              onChange={e => setSenhaLimpar(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && executarLimpeza()}
              autoFocus
              style={{
                width: '100%', padding: '7px 10px', fontSize: 14,
                border: '1px solid #ccc', borderRadius: 5, boxSizing: 'border-box', marginBottom: 8,
              }}
            />
            {erroLimpar && (
              <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{erroLimpar}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button
                style={{ padding: '6px 14px', cursor: 'pointer', borderRadius: 5,
                  border: '1px solid #ccc', background: '#f5f5f5', fontSize: 13 }}
                onClick={() => setModalLimpar(false)}
                disabled={limpando}
              >
                Cancelar
              </button>
              <button
                style={{ padding: '6px 16px', background: '#b91c1c', color: '#fff',
                  border: 'none', borderRadius: 5, cursor: limpando ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 'bold', opacity: limpando ? 0.7 : 1 }}
                onClick={executarLimpeza}
                disabled={limpando}
              >
                {limpando ? 'Limpando...' : 'Confirmar Limpeza'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
